import type { Resource } from 'i18next';

/* ──────────────────────────────────────────────────────────────────────
 * The intake form auto-translates option values via t('optionLabels.<value>')
 * and field labels (Field/ToggleChip) via t('fieldLabels.<JP-text>'). The JP
 * text is treated as the lookup key so the existing component code does not
 * need changing — translators just supply the en/ar mapping.
 * ────────────────────────────────────────────────────────────────────── */

const enOptionLabels = {
  // Materials
  plywood: 'Plywood',
  hardwood: 'Hardwood',
  softwood: 'Softwood',
  mdf: 'MDF',
  sheet_metal: 'Sheet metal',
  printed_pla: 'PLA',
  printed_abs: 'ABS',
  printed_petg: 'PETG',
  custom: 'Custom',
  // Fasteners
  screws: 'Screws',
  hidden: 'Hidden',
  glue_only: 'Glue only',
  mortise_tenon: 'Mortise & tenon',
  none: 'None',
  mixed: 'Mixed',
  // Shapes
  rectangular: 'Rectangular',
  round: 'Round',
  oval: 'Oval',
  square: 'Square',
  // Leg styles
  straight: 'Straight',
  tapered: 'Tapered',
  turned: 'Turned',
  trestle: 'Trestle',
  pedestal: 'Pedestal',
  splayed: 'Splayed',
  cabriole: 'Cabriole',
  panel: 'Panel',
  metal: 'Metal',
  // Joinery
  dowel: 'Dowel',
  biscuit: 'Biscuit',
  pocket_screw: 'Pocket screw',
  metal_bracket: 'Metal bracket',
  screw: 'Screw',
  dado: 'Dado',
  cam_lock: 'Cam lock',
  bed_bolt: 'Bed bolt',
  // Finish
  oil: 'Oil',
  lacquer: 'Lacquer',
  paint: 'Paint',
  wax: 'Wax',
  unfinished: 'Unfinished',
  melamine: 'Melamine',
  // Chair types
  dining: 'Dining',
  lounge: 'Lounge',
  office: 'Office',
  stool: 'Stool',
  rocking: 'Rocking',
  // Back styles
  spindle: 'Spindle',
  slat: 'Slat',
  upholstered: 'Upholstered',
  mesh: 'Mesh',
  // Shelf types
  bookcase: 'Bookcase',
  wall_mounted: 'Wall mounted',
  cube: 'Cube',
  ladder: 'Ladder',
  corner: 'Corner',
  // Bed sizes
  single: 'Single',
  semi_double: 'Semi-double',
  double: 'Double',
  queen: 'Queen',
  king: 'King',
  // Desk types
  simple: 'Simple',
  pedestal_single: 'Single pedestal',
  pedestal_double: 'Double pedestal',
  l_shape: 'L-shape',
  standing: 'Standing',
  // Cabinet types
  base: 'Base',
  wall: 'Wall',
  tall: 'Tall',
  island: 'Island',
  // Door styles
  flat_panel: 'Flat panel',
  shaker: 'Shaker',
  raised_panel: 'Raised panel',
  glass: 'Glass',
  sliding: 'Sliding',
  // Hinge styles
  concealed_hinge: 'Concealed hinge',
  butt_hinge: 'Butt hinge',
  piano_hinge: 'Piano hinge',
  cup_hinge: 'Cup hinge',
  // Bracket types
  L: 'L',
  T: 'T',
  flat: 'Flat',
  angle: 'Angle',
  gusset: 'Gusset',
  angled: 'Angled',
  offset: 'Offset',
  // Hole finish
  through: 'Through',
  countersunk: 'Countersunk',
  counterbored: 'Counterbored',
  threaded: 'Threaded',
  // Load directions
  shear: 'Shear',
  tension: 'Tension',
  cantilever: 'Cantilever',
  // Surface finishes
  as_machined: 'As machined',
  powder_coat: 'Powder coat',
  anodized: 'Anodized',
  plated: 'Plated',
  painted: 'Painted',
  ground: 'Ground',
  polished: 'Polished',
  // Housing types
  enclosure: 'Enclosure',
  gearbox: 'Gearbox',
  motor: 'Motor',
  pump: 'Pump',
  electronics_box: 'Electronics box',
  // Lid closures
  lid_screwed: 'Screwed lid',
  lid_snap_fit: 'Snap-fit lid',
  split_clamshell: 'Split clamshell',
  access_panel: 'Access panel',
  // Internal features
  mounting_bosses: 'Mounting bosses',
  cable_glands: 'Cable glands',
  vent: 'Vent',
  cooling_fins: 'Cooling fins',
  // Gear types
  spur: 'Spur',
  helical: 'Helical',
  bevel: 'Bevel',
  worm: 'Worm',
  rack: 'Rack',
  // Hub styles
  plain: 'Plain',
  set_screw: 'Set screw',
  keyed: 'Keyed',
  press_fit: 'Press fit',
  // Shaft types
  stepped: 'Stepped',
  splined: 'Splined',
  // Bearing fit
  press: 'Press',
  snug: 'Snug',
  transition: 'Transition',
  // End styles
  chamfered: 'Chamfered',
  // Fastener types
  bolt: 'Bolt',
  nut: 'Nut',
  washer: 'Washer',
  stud: 'Stud',
  rivet: 'Rivet',
  // Head shapes
  pan: 'Pan head',
  socket: 'Socket head',
  hex: 'Hex',
  button: 'Button head',
  cheese: 'Cheese head',
  // Threads
  full: 'Full',
  partial: 'Partial',
  // Finishes (fastener)
  zinc: 'Zinc plated',
  black_oxide: 'Black oxide',
  stainless: 'Stainless',
  titanium: 'Titanium',
  // Fixture types
  jig: 'Jig',
  clamp: 'Clamp',
  guide: 'Guide',
  stop: 'Stop',
  workholding: 'Workholding',
  // Clamping
  toggle: 'Toggle',
  cam: 'Cam',
  pneumatic: 'Pneumatic',
  magnetic: 'Magnetic',
  // Baseplate
  tslot: 'T-slot',
  screw_down: 'Screw down',
  free_standing: 'Free standing',
  // Tolerance class
  loose: 'Loose',
  standard: 'Standard',
  tight: 'Tight',
  precision: 'Precision',
  // Building types
  house: 'House',
  apartment: 'Apartment',
  retail: 'Retail',
  warehouse: 'Warehouse',
  mixed_use: 'Mixed use',
  // Roof styles
  gable: 'Gable',
  hip: 'Hip',
  shed: 'Shed',
  mansard: 'Mansard',
  butterfly: 'Butterfly',
  // Room types
  living: 'Living',
  bedroom: 'Bedroom',
  kitchen: 'Kitchen',
  bath: 'Bath',
  // Wall treatments
  wallpaper: 'Wallpaper',
  paneling: 'Paneling',
  tile: 'Tile',
  // Site types
  urban: 'Urban',
  suburban: 'Suburban',
  rural: 'Rural',
  park: 'Park',
  campus: 'Campus',
  // Facade detail types
  window: 'Window',
  door: 'Door',
  cornice: 'Cornice',
  parapet: 'Parapet',
  curtain_wall: 'Curtain wall',
  // Materials (architecture)
  masonry: 'Masonry',
  concrete: 'Concrete',
  wood: 'Wood',
  composite: 'Composite',
  // Decorative items
  vase: 'Vase',
  planter: 'Planter',
  art_object: 'Art object',
  ornament: 'Ornament',
  // Support policies
  avoid: 'Avoid',
  allow_minimal: 'Allow minimal',
  allow_full: 'Allow full',
  // Functional roles
  mount: 'Mount',
  adapter: 'Adapter',
  tool: 'Tool',
  hook: 'Hook',
  bracket: 'Bracket',
  gear: 'Gear',
  // Insert types
  heat_set: 'Heat-set',
  self_tapping: 'Self-tapping',
  // Replacement types
  knob: 'Knob',
  clip: 'Clip',
  cover: 'Cover',
  connector: 'Connector',
  // Fastener interface
  screw_M3: 'M3 screw',
  screw_M4: 'M4 screw',
  screw_M5: 'M5 screw',
  snap_fit: 'Snap fit',
  // Reinforcement
  thicker_walls: 'Thicker walls',
  ribs: 'Ribs',
  metal_insert: 'Metal insert',
  // Miniature types
  figure: 'Figure',
  terrain: 'Terrain',
  vehicle: 'Vehicle',
  building_kit: 'Building kit',
  modular_tile: 'Modular tile',
  // Detail levels
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  ultra: 'Ultra',
  // Enclosure for
  electronics: 'Electronics',
  storage: 'Storage',
  food_safe: 'Food safe',
  outdoor: 'Outdoor',
  general: 'General',
  // Closures (printing enclosure)
  magnet: 'Magnet',
  hinged_lid: 'Hinged lid',
  // Heat-set inserts
  heat_set_M2: 'Heat-set M2',
  heat_set_M3: 'Heat-set M3',
  heat_set_M4: 'Heat-set M4',
};

const jaOptionLabels = {
  plywood: '合板', hardwood: 'ハードウッド', softwood: 'ソフトウッド', mdf: 'MDF',
  sheet_metal: 'スチール板', printed_pla: 'PLA', printed_abs: 'ABS', printed_petg: 'PETG', custom: 'カスタム',
  screws: 'ネジ', hidden: '隠し', glue_only: '接着のみ', mortise_tenon: 'ほぞ継ぎ', none: 'なし', mixed: '混在',
  rectangular: '長方形', round: '円', oval: '楕円', square: '正方形',
  straight: 'ストレート', tapered: 'テーパー', turned: '丸棒', trestle: 'トレッスル', pedestal: '台座',
  splayed: '開き脚', cabriole: 'カブリオレ', panel: 'パネル', metal: '金属',
  dowel: 'ダボ', biscuit: 'ビスケット', pocket_screw: 'ポケットスクリュー', metal_bracket: '金属ブラケット',
  screw: 'ネジ', dado: '溝加工', cam_lock: 'カムロック', bed_bolt: 'ベッドボルト',
  oil: 'オイル', lacquer: 'ラッカー', paint: '塗装', wax: 'ワックス', unfinished: '未仕上げ', melamine: 'メラミン',
  dining: 'ダイニング', lounge: 'ラウンジ', office: 'オフィス', stool: 'スツール', rocking: 'ロッキング',
  spindle: 'スピンドル', slat: 'スラット', upholstered: '張地', mesh: 'メッシュ',
  bookcase: 'ブックケース', wall_mounted: '壁掛け', cube: 'キューブ', ladder: 'ラダー', corner: 'コーナー',
  single: 'シングル', semi_double: 'セミダブル', double: 'ダブル', queen: 'クイーン', king: 'キング',
  simple: 'シンプル', pedestal_single: '片袖', pedestal_double: '両袖', l_shape: 'L字', standing: 'スタンディング',
  base: 'ベース', wall: '壁掛け', tall: 'トール', island: 'アイランド',
  flat_panel: 'フラット', shaker: 'シェーカー', raised_panel: '框組み', glass: 'ガラス', sliding: '引き戸',
  concealed_hinge: '隠し蝶番', butt_hinge: '平丁番', piano_hinge: 'ピアノ蝶番', cup_hinge: 'カップ蝶番',
  L: 'L型', T: 'T型', flat: 'フラット', angle: 'アングル', gusset: 'ガセット', angled: 'アングル', offset: 'オフセット',
  through: '貫通', countersunk: '皿もみ', counterbored: 'ざぐり', threaded: 'ネジ穴',
  shear: 'せん断', tension: '引張', cantilever: '片持ち',
  as_machined: '切削のまま', powder_coat: '粉体塗装', anodized: 'アルマイト', plated: 'めっき', painted: '塗装',
  ground: '研削', polished: '研磨',
  enclosure: 'エンクロージャ', gearbox: 'ギアボックス', motor: 'モーター', pump: 'ポンプ', electronics_box: '電装ボックス',
  lid_screwed: 'ネジ止め蓋', lid_snap_fit: 'スナップ蓋', split_clamshell: '二つ割り', access_panel: 'アクセスパネル',
  mounting_bosses: '取付ボス', cable_glands: 'ケーブルグランド', vent: '通気口', cooling_fins: '冷却フィン',
  spur: '平歯車', helical: 'はすば歯車', bevel: 'かさ歯車', worm: 'ウォーム', rack: 'ラック',
  plain: 'プレーン', set_screw: 'セットスクリュー', keyed: 'キー溝', press_fit: '圧入',
  stepped: '段付き', splined: 'スプライン',
  press: '圧入', snug: 'スナッグ', transition: '中間',
  chamfered: '面取り',
  bolt: 'ボルト', nut: 'ナット', washer: 'ワッシャー', stud: 'スタッド', rivet: 'リベット',
  pan: 'なべ', socket: 'ソケット', hex: '六角', button: 'ボタン', cheese: 'チーズ',
  full: '全ネジ', partial: '半ネジ',
  zinc: 'ユニクロめっき', black_oxide: '黒染め', stainless: 'SUS', titanium: 'チタン',
  jig: '治具', clamp: 'クランプ', guide: 'ガイド', stop: 'ストッパー', workholding: 'ワーク保持',
  toggle: 'トグル', cam: 'カム', pneumatic: '空圧', magnetic: '磁気',
  tslot: 'Tスロット', screw_down: 'ネジ止め', free_standing: '自立',
  loose: 'ゆるい', standard: '標準', tight: 'タイト', precision: '高精度',
  house: '住宅', apartment: 'アパート', retail: '店舗', warehouse: '倉庫', mixed_use: '複合用途',
  gable: '切妻', hip: '寄棟', shed: '片流れ', mansard: 'マンサード', butterfly: 'バタフライ',
  living: 'リビング', bedroom: '寝室', kitchen: 'キッチン', bath: '浴室',
  wallpaper: '壁紙', paneling: 'パネル', tile: 'タイル',
  urban: '都市部', suburban: '郊外', rural: '農村', park: '公園', campus: 'キャンパス',
  window: '窓', door: '扉', cornice: 'コーニス', parapet: 'パラペット', curtain_wall: 'カーテンウォール',
  masonry: '組積造', concrete: 'コンクリート', wood: '木材', composite: '複合',
  vase: '花瓶', planter: 'プランター', art_object: 'アートオブジェ', ornament: 'オーナメント',
  avoid: '回避', allow_minimal: '最小限', allow_full: '全面許可',
  mount: 'マウント', adapter: 'アダプター', tool: '工具', hook: 'フック', bracket: 'ブラケット', gear: '歯車',
  heat_set: 'ヒートセット', self_tapping: 'セルフタッピング',
  knob: 'ノブ', clip: 'クリップ', cover: 'カバー', connector: 'コネクター',
  screw_M3: 'M3ネジ', screw_M4: 'M4ネジ', screw_M5: 'M5ネジ', snap_fit: 'スナップフィット',
  thicker_walls: '肉厚増加', ribs: 'リブ', metal_insert: '金属インサート',
  figure: 'フィギュア', terrain: '地形', vehicle: '乗り物', building_kit: '建物キット', modular_tile: 'モジュラータイル',
  low: '低', medium: '中', high: '高', ultra: '超高',
  electronics: '電子機器', storage: '収納', food_safe: '食品対応', outdoor: '屋外', general: '汎用',
  magnet: '磁石', hinged_lid: 'ヒンジ蓋',
  heat_set_M2: 'ヒートセット M2', heat_set_M3: 'ヒートセット M3', heat_set_M4: 'ヒートセット M4',
};

const arOptionLabels = {
  plywood: 'خشب رقائقي', hardwood: 'خشب صلب', softwood: 'خشب لين', mdf: 'MDF',
  sheet_metal: 'صفائح معدنية', printed_pla: 'PLA', printed_abs: 'ABS', printed_petg: 'PETG', custom: 'مخصص',
  screws: 'براغي', hidden: 'مخفي', glue_only: 'غراء فقط', mortise_tenon: 'نقرة ولسان',
  none: 'بدون', mixed: 'مختلط',
  rectangular: 'مستطيل', round: 'دائري', oval: 'بيضاوي', square: 'مربع',
  straight: 'مستقيم', tapered: 'مخروطي', turned: 'مخرط', trestle: 'حامل عارضة', pedestal: 'قاعدة مركزية',
  splayed: 'مائلة للخارج', cabriole: 'كابريول', panel: 'لوح', metal: 'معدن',
  dowel: 'وتد', biscuit: 'بسكويت', pocket_screw: 'برغي جيب', metal_bracket: 'حامل معدني',
  screw: 'برغي', dado: 'مجرى', cam_lock: 'قفل كام', bed_bolt: 'مسمار سرير',
  oil: 'زيت', lacquer: 'لكر', paint: 'دهان', wax: 'شمع', unfinished: 'غير مشطب', melamine: 'ميلامين',
  dining: 'طعام', lounge: 'استرخاء', office: 'مكتب', stool: 'كرسي بدون ظهر', rocking: 'هزاز',
  spindle: 'قضبان', slat: 'شرائح', upholstered: 'مبطن', mesh: 'شبكي',
  bookcase: 'مكتبة', wall_mounted: 'مثبت على الحائط', cube: 'مكعب', ladder: 'سلمي', corner: 'زاوية',
  single: 'مفرد', semi_double: 'نصف مزدوج', double: 'مزدوج', queen: 'كوين', king: 'كينغ',
  simple: 'بسيط', pedestal_single: 'خزانة جانبية واحدة', pedestal_double: 'خزانتان جانبيتان',
  l_shape: 'شكل L', standing: 'وقوف',
  base: 'قاعدة', wall: 'حائطي', tall: 'طويل', island: 'جزيرة',
  flat_panel: 'لوح مسطح', shaker: 'شاكر', raised_panel: 'لوح بارز', glass: 'زجاج', sliding: 'منزلق',
  concealed_hinge: 'مفصلة مخفية', butt_hinge: 'مفصلة عادية', piano_hinge: 'مفصلة بيانو', cup_hinge: 'مفصلة كوب',
  L: 'شكل L', T: 'شكل T', flat: 'مسطح', angle: 'زاوية', gusset: 'مدعم', angled: 'مائل', offset: 'مزاح',
  through: 'نافذ', countersunk: 'غاطس', counterbored: 'بتجويف رأس', threaded: 'قلاوظ',
  shear: 'قص', tension: 'شد', cantilever: 'كابولي',
  as_machined: 'كما هو مشغل', powder_coat: 'طلاء بودرة', anodized: 'مؤكسد', plated: 'مطلي', painted: 'مدهون',
  ground: 'مجلوخ', polished: 'مصقول',
  enclosure: 'علبة', gearbox: 'علبة تروس', motor: 'محرك', pump: 'مضخة', electronics_box: 'علبة إلكترونيات',
  lid_screwed: 'غطاء ببراغي', lid_snap_fit: 'غطاء كبس', split_clamshell: 'نصفان', access_panel: 'لوحة وصول',
  mounting_bosses: 'دعامات تثبيت', cable_glands: 'مداخل كابل', vent: 'تهوية', cooling_fins: 'زعانف تبريد',
  spur: 'مستقيم', helical: 'حلزوني', bevel: 'مخروطي', worm: 'دودي', rack: 'مسنن خطي',
  plain: 'عادي', set_screw: 'برغي تثبيت', keyed: 'بمفتاح', press_fit: 'كبس',
  stepped: 'متدرج', splined: 'مسنن',
  press: 'كبس', snug: 'محكم', transition: 'انتقالي',
  chamfered: 'مشطوف',
  bolt: 'مسمار', nut: 'صامولة', washer: 'واشرة', stud: 'قضيب ملولب', rivet: 'برشام',
  button: 'رأس كروي', pan: 'رأس مستدير', socket: 'سداسي داخلي', hex: 'سداسي', cheese: 'رأس أسطواني',
  full: 'كامل', partial: 'جزئي',
  zinc: 'زنك', black_oxide: 'أكسيد أسود', stainless: 'ستانلس', titanium: 'تيتانيوم',
  jig: 'جيغ', clamp: 'مشبك', guide: 'دليل', stop: 'محدد', workholding: 'تثبيت عمل',
  toggle: 'تبديل', cam: 'كام', pneumatic: 'هوائي', magnetic: 'مغناطيسي',
  tslot: 'T-slot', screw_down: 'تثبيت ببراغي', free_standing: 'مستقل',
  loose: 'واسع', standard: 'قياسي', tight: 'دقيق', precision: 'عالي الدقة',
  house: 'منزل', apartment: 'شقة', retail: 'تجاري', warehouse: 'مستودع', mixed_use: 'استخدام مختلط',
  gable: 'جملوني', hip: 'هرمي', shed: 'مائل', mansard: 'مانسارد', butterfly: 'فراشة',
  living: 'معيشة', bedroom: 'غرفة نوم', kitchen: 'مطبخ', bath: 'حمام',
  wallpaper: 'ورق جدران', paneling: 'تكسية ألواح', tile: 'بلاط',
  urban: 'حضري', suburban: 'ضواحي', rural: 'ريفي', park: 'حديقة', campus: 'حرم',
  window: 'نافذة', door: 'باب', cornice: 'كورنيش', parapet: 'درابزين سطح', curtain_wall: 'واجهة ستارية',
  masonry: 'مباني', concrete: 'خرسانة', wood: 'خشب', composite: 'مركب',
  vase: 'مزهرية', planter: 'حوض نبات', art_object: 'قطعة فنية', ornament: 'زخرفة',
  avoid: 'تجنب', allow_minimal: 'أقل دعم', allow_full: 'دعم كامل',
  mount: 'حامل', adapter: 'محوّل', tool: 'أداة', hook: 'خطاف', bracket: 'حامل', gear: 'ترس',
  heat_set: 'حراري', self_tapping: 'ذاتي اللولبة',
  knob: 'مقبض', clip: 'مشبك', cover: 'غطاء', connector: 'موصل',
  screw_M3: 'برغي M3', screw_M4: 'برغي M4', screw_M5: 'برغي M5', snap_fit: 'تعشيق كبس',
  thicker_walls: 'جدران أسمك', ribs: 'أضلاع تقوية', metal_insert: 'جلبة معدنية',
  figure: 'شخصية', terrain: 'تضاريس', vehicle: 'مركبة', building_kit: 'مجموعة بناء', modular_tile: 'بلاطة معيارية',
  low: 'منخفض', medium: 'متوسط', high: 'عال', ultra: 'فائق',
  electronics: 'إلكترونيات', storage: 'تخزين', food_safe: 'آمن للطعام', outdoor: 'خارجي', general: 'عام',
  magnet: 'مغناطيس', hinged_lid: 'غطاء بمفصلة',
  heat_set_M2: 'حراري M2', heat_set_M3: 'حراري M3', heat_set_M4: 'حراري M4',
};

const enFieldLabels = {
  '用途': 'Use',
  '寸法': 'Size',
  '素材': 'Material',
  '接合方式（任意）': 'Fastening method (optional)',
  '座席数': 'Seats',
  '形状': 'Shape',
  '脚スタイル': 'Leg style',
  '接合方法': 'Joinery',
  '仕上げ': 'Finish',
  '幕板': 'Stretcher',
  'エプロン': 'Apron',
  '椅子タイプ': 'Chair type',
  '背もたれスタイル': 'Back style',
  '肘掛け': 'Armrests',
  '棚タイプ': 'Shelf type',
  '段数': 'Shelf count',
  '可動棚': 'Adjustable shelves',
  '背板あり': 'Back panel',
  'ベッドサイズ': 'Bed size',
  'スラット数': 'Slat count',
  'ヘッドボード': 'Headboard',
  'フットボード': 'Footboard',
  '収納付き': 'Under-bed storage',
  'デスクタイプ': 'Desk type',
  '引き出し数': 'Drawer count',
  'ケーブル管理': 'Cable management',
  'キャビネットタイプ': 'Cabinet type',
  '扉スタイル': 'Door style',
  '扉数': 'Door count',
  '内部棚数': 'Interior shelves',
  '丁番スタイル': 'Hinge style',
  'ブラケットタイプ': 'Bracket type',
  '取付パターン': 'Mounting pattern',
  '穴数': 'Hole count',
  '穴サイズ': 'Hole size',
  '穴仕上げ': 'Hole finish',
  '荷重方向': 'Load direction',
  '表面処理': 'Surface finish',
  '筐体タイプ': 'Housing type',
  '蓋の固定': 'Lid closure',
  'IP等級': 'IP rating',
  '内部フィーチャー': 'Internal features',
  'ファスナーサイズ': 'Fastener size',
  '歯車タイプ': 'Gear type',
  'モジュール': 'Module',
  '歯数': 'Tooth count',
  '圧力角': 'Pressure angle',
  '歯幅 (mm)': 'Face width (mm)',
  '軸穴径 (mm)': 'Bore diameter (mm)',
  'ハブスタイル': 'Hub style',
  'シャフトタイプ': 'Shaft type',
  '直径 (mm)': 'Diameter (mm)',
  'キー溝あり': 'Keyway',
  '軸受けはめあい': 'Bearing fit',
  '端部処理': 'End style',
  'ファスナータイプ': 'Fastener type',
  'サイズ': 'Size',
  '長さ (mm)': 'Length (mm)',
  '頭部形状': 'Head shape',
  'ネジ部': 'Thread',
  '治具タイプ': 'Fixture type',
  'ワーク最大長 (mm)': 'Max workpiece length (mm)',
  'ワーク最大幅 (mm)': 'Max workpiece width (mm)',
  'クランプ方式': 'Clamping method',
  'ベース取付': 'Baseplate mounting',
  '公差クラス': 'Tolerance class',
  '位置決めピン': 'Alignment pins',
  '建物タイプ': 'Building type',
  '縮尺': 'Scale',
  '階数': 'Floors',
  '屋根スタイル': 'Roof style',
  '内装表示': 'Show interior',
  '外構表示': 'Show landscape',
  '室タイプ': 'Room type',
  '天井高 (mm)': 'Ceiling height (mm)',
  '家具表示': 'Show furniture',
  '壁仕上げ': 'Wall treatment',
  '天井表示': 'Show ceiling',
  '敷地タイプ': 'Site type',
  '地形表示': 'Show topography',
  '植栽表示': 'Show vegetation',
  '道路表示': 'Show roads',
  '建物棟数': 'Building count',
  '詳細タイプ': 'Detail type',
  '断面表示': 'Show section',
  '層構成表示': 'Show layers',
  'アイテムタイプ': 'Item type',
  'プリンター': 'Printer',
  '積層ピッチ (mm)': 'Layer height (mm)',
  'シェル厚 (mm)': 'Shell thickness (mm)',
  'サポート方針': 'Support policy',
  '花瓶モード対応': 'Vase mode compatible',
  '機能的役割': 'Functional role',
  'インフィル (%)': 'Infill (%)',
  'インサートタイプ': 'Insert type',
  '交換部品タイプ': 'Replacement type',
  'ファスナーインターフェース': 'Fastener interface',
  '補強': 'Reinforcement',
  '寸法一致': 'Match original dimensions',
  'ミニチュアタイプ': 'Miniature type',
  'スケール': 'Scale',
  '細部レベル': 'Detail level',
  'ベースあり': 'Base present',
  '通気口あり': 'Vents',
  'ケーブル引出し': 'Cable entry',
  '取付ボス': 'Mounting bosses',
  'ケーブルグランド': 'Cable glands',
  '通気口': 'Vent',
  '冷却フィン': 'Cooling fins',
};

const jaFieldLabels: Record<string, string> = Object.fromEntries(
  Object.keys(enFieldLabels).map((k) => [k, k]),
);

const arFieldLabels = {
  '用途': 'الاستخدام',
  '寸法': 'الأبعاد',
  '素材': 'المادة',
  '接合方式（任意）': 'طريقة التثبيت (اختياري)',
  '座席数': 'عدد المقاعد',
  '形状': 'الشكل',
  '脚スタイル': 'نمط الأرجل',
  '接合方法': 'طريقة الوصل',
  '仕上げ': 'التشطيب',
  '幕板': 'عارضة',
  'エプロン': 'مريلة',
  '椅子タイプ': 'نوع الكرسي',
  '背もたれスタイル': 'نمط الظهر',
  '肘掛け': 'مساند أذرع',
  '棚タイプ': 'نوع الرف',
  '段数': 'عدد الرفوف',
  '可動棚': 'رفوف قابلة للتعديل',
  '背板あり': 'لوح خلفي',
  'ベッドサイズ': 'حجم السرير',
  'スラット数': 'عدد الشرائح',
  'ヘッドボード': 'لوح الرأس',
  'フットボード': 'لوح القدم',
  '収納付き': 'تخزين سفلي',
  'デスクタイプ': 'نوع المكتب',
  '引き出し数': 'عدد الأدراج',
  'ケーブル管理': 'إدارة الكابلات',
  'キャビネットタイプ': 'نوع الخزانة',
  '扉スタイル': 'نمط الباب',
  '扉数': 'عدد الأبواب',
  '内部棚数': 'عدد الرفوف الداخلية',
  '丁番スタイル': 'نمط المفصلة',
  'ブラケットタイプ': 'نوع الحامل',
  '取付パターン': 'نمط التثبيت',
  '穴数': 'عدد الثقوب',
  '穴サイズ': 'حجم الثقب',
  '穴仕上げ': 'تشطيب الثقب',
  '荷重方向': 'اتجاه الحمل',
  '表面処理': 'معالجة السطح',
  '筐体タイプ': 'نوع الغلاف',
  '蓋の固定': 'تثبيت الغطاء',
  'IP等級': 'تصنيف IP',
  '内部フィーチャー': 'الميزات الداخلية',
  'ファスナーサイズ': 'حجم المثبت',
  '歯車タイプ': 'نوع الترس',
  'モジュール': 'الموديول',
  '歯数': 'عدد الأسنان',
  '圧力角': 'زاوية الضغط',
  '歯幅 (mm)': 'عرض الوجه (mm)',
  '軸穴径 (mm)': 'قطر الثقب المحوري (mm)',
  'ハブスタイル': 'نمط المحور',
  'シャフトタイプ': 'نوع العمود',
  '直径 (mm)': 'القطر (mm)',
  'キー溝あり': 'مجرى مفتاح',
  '軸受けはめあい': 'ملاءمة المحمل',
  '端部処理': 'تشطيب الطرف',
  'ファスナータイプ': 'نوع المثبت',
  'サイズ': 'الحجم',
  '長さ (mm)': 'الطول (mm)',
  '頭部形状': 'شكل الرأس',
  'ネジ部': 'القلاوظ',
  '治具タイプ': 'نوع الجيغ',
  'ワーク最大長 (mm)': 'أقصى طول للقطعة (mm)',
  'ワーク最大幅 (mm)': 'أقصى عرض للقطعة (mm)',
  'クランプ方式': 'طريقة التثبيت',
  'ベース取付': 'تثبيت القاعدة',
  '公差クラス': 'فئة التفاوت',
  '位置決めピン': 'مسامير تموضع',
  '建物タイプ': 'نوع المبنى',
  '縮尺': 'المقياس',
  '階数': 'عدد الطوابق',
  '屋根スタイル': 'نمط السقف',
  '内装表示': 'إظهار الداخل',
  '外構表示': 'إظهار الموقع',
  '室タイプ': 'نوع الغرفة',
  '天井高 (mm)': 'ارتفاع السقف (mm)',
  '家具表示': 'إظهار الأثاث',
  '壁仕上げ': 'تشطيب الجدار',
  '天井表示': 'إظهار السقف',
  '敷地タイプ': 'نوع الموقع',
  '地形表示': 'إظهار التضاريس',
  '植栽表示': 'إظهار النباتات',
  '道路表示': 'إظهار الطرق',
  '建物棟数': 'عدد المباني',
  '詳細タイプ': 'نوع التفصيل',
  '断面表示': 'إظهار المقطع',
  '層構成表示': 'إظهار الطبقات',
  'アイテムタイプ': 'نوع العنصر',
  'プリンター': 'الطابعة',
  '積層ピッチ (mm)': 'ارتفاع الطبقة (mm)',
  'シェル厚 (mm)': 'سماكة الغلاف (mm)',
  'サポート方針': 'سياسة الدعم',
  '花瓶モード対応': 'متوافق مع وضع المزهرية',
  '機能的役割': 'الدور الوظيفي',
  'インフィル (%)': 'نسبة الحشو (%)',
  'インサートタイプ': 'نوع الجلبة',
  '交換部品タイプ': 'نوع القطعة البديلة',
  'ファスナーインターフェース': 'واجهة المثبت',
  '補強': 'التقوية',
  '寸法一致': 'مطابقة الأبعاد الأصلية',
  'ミニチュアタイプ': 'نوع المجسم الصغير',
  'スケール': 'المقياس',
  '細部レベル': 'مستوى التفاصيل',
  'ベースあり': 'مع قاعدة',
  '通気口あり': 'مع فتحات تهوية',
  'ケーブル引出し': 'مدخل كابل',
  '取付ボス': 'دعامات تثبيت',
  'ケーブルグランド': 'مداخل كابل',
  '通気口': 'فتحات تهوية',
  '冷却フィン': 'زعانف تبريد',
};

export const resources = {
  en: {
    common: {
      home: 'Home',
      newProject: 'New project',
      projects: 'Projects',
      collapse: 'Collapse',
      expandSidebar: 'Expand sidebar',
      logout: 'Log out',
      language: 'Language',
      desktopOnly: 'Please open AgentCAD in a desktop browser.',
      untitledProject: 'Untitled project',
      thinking: 'Thinking',
      checkpointSaved: 'Checkpoint saved',
      parts: 'parts',
      joints: 'joints',
      unsetCategory: 'Unset',
      brand: 'AgentCAD',
      units: { piece: '', count: '', shelves: '', slats: '', floors: '', buildings: '', teeth: '' },
      chat: {
        messagePlaceholder: 'Keep refining with AgentCAD...',
        interruptRunning: 'Agent running — you can steer it mid-run',
        interruptPlaceholder: 'Example: change the wood to walnut / make the legs cylindrical...',
        sentPrefix: 'Sent: {{text}}',
        modifyQuestion: 'What should change?',
        modifyPlaceholder: 'Example: make the legs slimmer and set height to 700mm...',
        send: 'Send',
        back: 'Back',
        continue: 'Continue',
        modify: 'Modify',
        turboTitle: 'Continue automatically from now on',
        turboMode: 'Turbo mode',
        done: 'Done',
        freeAnswerPlaceholder: 'Free answer...',
        fixWithAI: 'Fix with AI',
        edit: 'Edit',
        copyContent: 'Copy content',
        continueDefault: 'Please continue.',
        errorOccurred: 'An error occurred',
        retry: 'Retry',
        defaultObjectTitle: 'AgentCAD object',
        chatTitleFallback: 'Chat',
        suggestionsHint: 'Pick an option to answer',
        suggestionsSubmit: 'Send answer',
        welcomePlaceholder: 'What will you build with AgentCAD today?',
        imageUploadFailed: 'Image upload failed',
        renderUploadFailed: 'Render upload failed',
        modelProcessingError: '3D model processing error',
        singleModelTitle: 'Only one 3D model at a time',
        singleModelDesc: 'Remove the current model before adding another.',
        multipleModelsTitle: 'Multiple 3D models',
        multipleModelsDesc: 'Only the first STL file was added.',
        imageTooSmallTitle: 'Image too small',
        imageTooSmallDesc: 'Images smaller than 256×256 pixels were not added.',
        imageTooLargeTitle: 'Image too large',
        imageTooLargeDesc: 'Images larger than 100MB were not added.',
        invalidImageTitle: 'Invalid image format',
        invalidImageDesc: 'Images that are not a valid format were not added. They must be jpeg, png, or webp.',
        addImageHere: 'Add an image here',
        dropImagesOrModels: 'Drop images or 3D models here',
        modelViewAlt: '3D model view',
        imageAlt: 'Image',
        imageNotFound: 'Image not found',
        messagesCount: '{{count}} messages',
      },
      toolNames: {
        add_part: 'Add part',
        modify_part: 'Modify part',
        remove_part: 'Remove part',
        add_joint: 'Add joint',
        modify_joint: 'Modify joint',
        remove_joint: 'Remove joint',
        set_parameter: 'Set parameter',
        set_view: 'Set view',
        validate: 'Validate',
        render: 'Render',
        ask_user: 'Question',
      },
      toast: { error: 'Error', success: 'Success' },
      dialog: {
        cancel: 'Cancel',
        delete: 'Delete',
        save: 'Save',
        confirm: 'Confirm',
      },
      history: {
        title: 'Past projects',
        list: 'List',
        visual: 'Visual',
        searchPlaceholder: 'Search projects...',
        noMatchTitle: 'No matching conversations found',
        noMatchHint: 'Try a different search term',
        emptyTitle: 'No conversations yet',
        emptyHint: 'Start a new chat to create CAD',
        startNewChat: 'Start a new chat',
        newProjectAria: 'Create a new project',
        deleteConversationTitle: 'Delete conversation',
        deleteConversationDesc: 'Are you sure you want to delete this conversation? This cannot be undone.',
        deleteProjectTitle: 'Delete project',
        deleteProjectDesc: 'Are you sure you want to delete this project? This cannot be undone.',
        renameTitle: 'Rename project',
        renameDesc: 'Enter a new name for this project.',
        renamePlaceholder: 'Conversation name',
        rename: 'Rename',
        compiling: 'Compiling...',
        loadFailed: 'Failed to load conversations',
        deleted: 'Conversation deleted',
        deleteFailed: 'Failed to delete conversation',
        renamed: 'Conversation renamed',
        renameFailed: 'Failed to rename conversation',
        titleEmpty: 'Title cannot be empty',
        dateFormat: 'MMMM d, yyyy',
      },
      editor: {
        notFound: 'Conversation not found',
        chatVertical: 'Chat',
        outputVertical: 'Output',
        expandChat: 'Expand chat panel',
        collapseChat: 'Collapse chat panel',
        expandOutput: 'Expand output panel',
        collapseOutput: 'Collapse output panel',
      },
      viewer: {
        generating: 'Generating model',
        empty: 'Send a prompt in chat to generate a CAD model',
        compiling: 'Compiling...',
        compileError: 'Compile error',
        compileErrorDesc: 'AgentCAD detected an error while compiling',
        fixWithAI: 'Fix with AI',
        view3D: '3D view',
        view2D: '2D drawing',
        nextImage: 'Next image',
        previousImage: 'Previous image',
        rendering: 'Rendering',
        loadingModel: 'Loading model',
        perspective: 'Perspective view',
        orthographic: 'Orthographic view',
        gizmo: { right: 'R', left: 'L', top: 'T', bottom: 'B', front: 'F', back: 'Bk' },
        surfaceImages: {
          add: 'Add surface image',
          select: 'Select image {{index}}',
          remove: 'Remove surface image',
          scale: 'Scale',
          rotate: 'Rotate',
          opacity: 'Opacity',
        },
        feedback: {
          improve3D: ['Make walls thicker', 'Round the corners', 'Add more detail', 'Make it bigger'],
          improve2D: ['Add dimensions', 'Add a section view', 'Add more views', 'Improve annotations'],
          improve3DPrefix: '[3D model improvements] ',
          improve2DPrefix: '[2D drawing improvements] ',
          placeholder3D: 'Enter model improvements...',
          placeholder2D: 'Enter drawing improvements...',
          improveButton: 'Improve with AI',
        },
      },
      parameter: {
        color: 'Color',
        controls: 'Parameters',
        noEditable: 'No editable parameters yet.',
        reset: 'Reset parameter',
        openColorPicker: 'Open color picker',
        download: 'Download',
        downloadAria: 'Download {{format}}',
      },
      output: {
        title: 'Output',
        appearance: 'Appearance',
        export: 'Export',
        formatGroupAria: 'Export format',
        exportEmpty: 'Nothing to export yet — generate a model first.',
        exportHint: 'Saves the current {{format}} of this model.',
      },
      spec: {
        visionEval: 'Vision evaluation',
        partsLabel: 'parts',
        jointsLabel: 'joints',
        specPrefix: 'Spec · ',
        specPartsSuffix: ' parts / ',
        specJointsSuffix: ' joints',
        visualNotes: 'visual note(s)',
        noStructured: 'This message has no structured Spec.',
        noJoints: '(none)',
        noParameters: '(none)',
        noMaterials: '(none)',
        treeParts: 'parts',
        treeJoints: 'joints',
        treeParameters: 'parameters',
        treeMaterials: 'materials',
        opening: 'opening',
        viewFront: 'Front',
        viewTop: 'Top',
        viewRight: 'Right',
        viewIso: 'Iso',
        viewLayout: 'Layout',
      },
      drawing: {
        bomGenerate: 'Generate BOM',
        bomTitle: 'Bill of materials',
        bomParts: 'parts',
        bomNo: 'No.',
        bomName: 'Name',
        bomSpec: 'Spec',
        bomQty: 'Qty',
        bomMaterial: 'Material',
        bomDimensions: 'Dimensions',
        bomNotes: 'Notes',
        partName: 'Part name',
        addRow: 'Add row',
        confirmAndContinue: 'Confirm',
        customInput: 'Custom input...',
        attachments: 'Attachments',
        weight: 'Weight: ',
        untitled: 'Untitled',
        dateLabel: 'Y M D',
        scaleLabel: 'Scale',
        itemNameLabel: 'Item',
        drawingNumberLabel: 'Drawing #',
        draftedBy: 'Drafter',
        checkedBy: 'Checker',
        approvedBy: 'Approver',
        manufacturingDrawing: 'Manufacturing drawing',
        regenerate: 'Regenerate',
        generating: 'Generating…',
        generate: 'Generate',
        strictModeError: '⚠ Strict mode found {{count}} error(s)',
        bomReview: 'Review/edit BOM',
        fitToView: 'Fit to view',
        exportSVG: 'Export SVG',
        exportPDF: 'Export PDF',
        statusQueued: 'Queued',
        statusRunning: 'Running',
        statusReady: 'Ready',
        statusFailed: 'Failed',
        cutListPartsTable: 'Generate parts table',
        cutListHeaderNumber: '#',
        cutListHeaderPart: 'Part',
        cutListHeaderKind: 'Kind',
        cutListHeaderWHT: 'W×H×T',
        cutListHeaderMaterial: 'Material',
        cutListHeaderQty: 'Qty',
        cutListFlattenSkipped: 'Flatten skipped: ',
      },
      services: {
        loginRequired: 'You need to sign in.',
        conversationIdRequired: 'A conversation id is required',
        conversationNotFound: 'Conversation not found',
        chatApiError: 'Chat API error',
        chatNoBody: 'Could not get response body',
        chatNoResponse: 'Did not receive a response from the chat function',
        errorPrefix: 'Error: ',
        intendedUseRequired: 'Please enter a use',
        cannotUpdateConversation: 'Cannot update the conversation',
        intakeMismatch: 'category and subType do not match details',
        untitledDesign: 'Untitled design',
        unitMm: 'mm',
        webglUnavailable: 'WebGL is not available. Please use a browser that supports WebGL.',
        noOutput: 'No output',
        drawingJobNotFound: 'Drawing job not found',
        noSectionOutput: 'No output from section compilation',
        noProjectionOutput: 'No output from projection compilation',
        noFileInArtifact: 'No {{ext}} file in artifact',
      },
      meshChat: {
        intro:
          "Mesh models are sculpted, not parametric. Editing isn't supported in this mode. Re-generate for a different result, or start a new conversation with a text description for a parametric model.",
        regenerateButton: 'Regenerate',
        regenerateFailedTitle: "Couldn't start regeneration",
        downloadAria: 'Download {{format}}',
        downloadFailedTitle: "Couldn't download {{format}}",
      },
      meshError: {
        messageFailed: 'Mesh generation failed. Try a different image.',
        messageBanned:
          'Image was rejected by the mesh service. Try a different image.',
        messageExpired: "Mesh generation didn't complete. Try again.",
        messageCancelled: "Mesh generation didn't complete. Try again.",
        messageUnknown: "Mesh generation didn't complete. Try again.",
        messageGeneric: 'Mesh generation failed.',
        retryButton: 'Retry',
        dismissButton: 'Dismiss',
        codeSuffix: '(code {{code}})',
      },
      meshUnavailable: {
        message:
          "Mesh isn't available right now. Try regenerating from the chat panel.",
        reloadButton: 'Reload',
      },
    },
    prompt: {
      morning: 'Good morning',
      afternoon: 'Hello',
      evening: 'Good evening',
      subtitlePrefix: 'Fill the form to generate ',
      subtitleDrawing: '2D drawings',
      subtitleMiddle: ' and ',
      subtitleModel: '3D models',
      quickExamples: 'Quick examples:',
      examples: {
        gear: { label: 'Gear', intendedUse: 'Parametric spur gear' },
        vase: { label: 'Vase', intendedUse: 'Organic shaped vase' },
        architectureModel: { label: 'Architecture model', intendedUse: 'Modern house architecture model' },
        bracket: { label: 'Bracket', intendedUse: 'Mounting L-bracket' },
        cabinet: { label: 'Wall cabinet', intendedUse: 'Two-door wall-mounted cabinet' },
        diningTable: { label: 'Dining table', intendedUse: 'Dining table for six people' },
      },
      loginRequiredTitle: 'Error',
      loginRequiredDescription: 'You need to sign in.',
      generationFailed: 'Generation failed',
      newConversationTitle: 'New conversation',
      textStart: {
        headline: 'Or just describe what you want',
        subline:
          "Type a sentence or two. Include size, material, and what it's for if you can. We'll start building from your description.",
        placeholder:
          'e.g. A 1200×800×720mm oak dining table with four square tapered legs.',
        buildButton: 'Build it from this',
        buildingButton: 'Starting…',
      },
      imageStart: {
        headline: 'Have a reference image?',
        subline:
          'Drop a photo to generate a sculpted 3D mesh (not parametrically editable).',
        starting: 'Starting mesh generation…',
        pickButton: 'Choose image',
        invalidTypeTitle: 'Unsupported image type',
        invalidTypeDescription: 'Please upload a JPEG, PNG, or WEBP file.',
        tooLargeTitle: 'Image too large',
        tooLargeDescription: 'Maximum size is 4 MB. Resize and try again.',
        kickoffFailedTitle: "Couldn't start mesh generation",
        kickoffFailedWhile: '(while {{step}})',
        confirmHeadline: 'Ready to generate?',
        confirmButton: 'Generate 3D mesh',
        clearButton: 'Clear',
        previewAlt: 'Uploaded reference image preview',
        uploading: 'Uploading image…',
        uploadFailedTitle: "Couldn't upload image",
        steps: {
          createConversation: 'creating the draft conversation',
          uploadImage: 'uploading the image',
          startMesh: 'starting mesh generation',
        },
      },
      meshModeToggle: {
        label: 'Mesh mode (image-to-3D)',
        imageHint: 'Turn on Mesh mode to upload an image.',
        textHint: 'Turn off Mesh mode to use text intake.',
      },
    },
    intake: {
      category: '1. Category',
      subType: '2. Type',
      quality: '3. Quality level',
      core: '4. Core details',
      details: '5. Details',
      drawing: '6. Drawing preferences',
      freeform: '7. Notes (optional)',
      inputError: 'Input error',
      generate: 'Generate',
      intent: 'Request',
      intentPlaceholder: 'Optional: dining table for six people, shelf bracket, electronics case...',
      advanced: 'Advanced details',
      hideAdvanced: 'Hide advanced',
      coreLabels: {
        intendedUse: 'Use',
        intendedUsePlaceholder: 'Dining table for six people',
        size: 'Size',
        material: 'Material',
        fastenerPreference: 'Fastening method (optional)',
      },
      dimensions: { length: 'L', width: 'W', height: 'H' },
      freeformPlaceholder: 'Add requirements that do not fit the form here (max 1000 characters)',
      categories: {
        furniture: { label: 'Furniture / Woodwork', hint: 'Tables, chairs, shelves' },
        mechanical: { label: 'Mechanical parts', hint: 'Brackets, gears, housings' },
        architecture: { label: 'Architecture models', hint: 'Homes, buildings, sites' },
        printing: { label: '3D printing', hint: 'Objects, stands, bases' },
      },
      subTypes: {
        table: 'Table', chair: 'Chair', shelf: 'Shelf', bed: 'Bed', desk: 'Desk', cabinet: 'Cabinet',
        bracket: 'Bracket', housing: 'Housing', gear: 'Gear', shaft: 'Shaft',
        fastener_part: 'Fastener', fixture: 'Fixture',
        building: 'Building', interior_room: 'Interior', site: 'Site', facade_detail: 'Facade detail',
        decorative: 'Decorative', functional: 'Functional part', replacement: 'Replacement part',
        miniature: 'Miniature', enclosure: 'Enclosure',
      },
      qualityLevels: {
        draft: { label: 'Draft', hint: 'Fast simple shape' },
        standard: { label: 'Standard', hint: 'Normal detail' },
        manufacturing: { label: 'Manufacturing', hint: 'Dimensions, tolerances, BOM' },
        extreme: { label: 'Extreme', hint: 'Screws, joints, all details' },
      },
      drawingPrefs: {
        scope: 'Scope',
        section: 'Sections',
        tolerance: 'Tolerance class',
        holeTable: 'Hole table',
        bom: 'BOM',
        jisStrict: 'JIS strict',
        scopes: { assembly_only: 'Assembly only', parts_only: 'Parts only', both: 'Both' },
        sections: { auto: 'Auto', none: 'None' },
        tolerances: { loose: 'Loose', standard: 'Standard', tight: 'Tight', precision: 'Precision' },
      },
      optionLabels: enOptionLabels,
      fieldLabels: enFieldLabels,
      units: {
        '段': 'shelves',
        '本': 'slats',
        '個': '',
        '枚': '',
        '階': 'floors',
        '棟': 'buildings',
      },
    },
    auth: {
      heroTitleLine1: 'Co-create with AI,',
      heroTitleLine2: 'shape your ideas.',
      heroSubtitle: 'A CAD agent that turns text into 2D drawings and 3D models in seconds.',
      signupTitle: 'Create account',
      loginTitle: 'Welcome back',
      signupSubtitle: 'Start your 7-day free trial',
      loginSubtitle: 'Sign in to your account',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: '6 characters or more',
      submitSignup: 'Create account',
      submitLogin: 'Sign in',
      submitting: '...',
      hasAccount: 'Already have an account?',
      noAccount: "Don't have an account yet",
      toggleToLogin: 'Sign in',
      toggleToSignup: 'Create account',
      errors: {
        invalidCredential: 'Email or password is incorrect',
        emailInUse: 'This email is already in use',
        weakPassword: 'Password must be at least 6 characters',
        tooManyRequests: 'Too many requests. Please try again later',
        unknown: 'An error occurred. Please try again',
      },
    },
    landing: {
      navLogin: 'Log in',
      navStartFree: 'Start free',
      heroTitle1: 'Just words,',
      heroTitle2: 'design.',
      heroSubtitle: 'Generate parametric 2D drawings and 3D models from text in seconds. AgentCAD changes how design is done.',
      heroCta: '7-day free trial',
      heroCtaNote: 'No credit card required',
      statTimeSaved: 'Design time saved',
      statSetup: 'Setup time',
      statSetupSuffix: 's',
      statCost: 'Initial cost',
      statCostSuffix: '$',
      statTimeSuffix: '%',
      featuresEyebrow: 'Features',
      featuresHeadline: 'Everything starts from text.',
      features: {
        textTo2d3d: {
          title: 'Text to 2D & 3D',
          tag: 'Bring what you imagine into being.',
          desc: 'Just describe in text — AI auto-generates an OpenSCAD-based parametric model and 2D drawing.',
        },
        realtime: {
          title: 'Real-time editing',
          tag: 'Tweak instantly.',
          desc: 'Adjust size, shape, and angle in real time with sliders. No regeneration wait.',
        },
        multiAngle: {
          title: 'Multi-angle view',
          tag: 'Verify from every angle.',
          desc: '360° free rotation view — confirm in real time from any angle.',
        },
        export: {
          title: 'Export',
          tag: 'Ready to ship.',
          desc: 'One-click export to PDF and STL.',
        },
      },
      stepsEyebrow: 'How it works',
      stepsHeadline: 'Done in 3 steps.',
      steps: {
        describe: { title: 'Describe in text', desc: 'Type what you want to build. No expert knowledge needed.' },
        generate: { title: 'AI auto-generates', desc: '2D drawings and 3D models in seconds. Parametric.' },
        editExport: { title: 'Edit & export', desc: 'Tune the parameters and download the final piece.' },
      },
      useCasesEyebrow: 'Use cases',
      useCasesHeadline: 'Why teams choose AgentCAD',
      useCasesSub: 'Your dedicated CAD agent, on duty 24/7.',
      useCases: {
        prototype: {
          title: 'Prototype 10× faster',
          audience: 'For product teams',
          desc: 'Turn ideas into 3D models instantly. Slash your iteration cycle.',
        },
        nonExpert: {
          title: 'No CAD expertise needed',
          audience: 'For designers & PMs',
          desc: 'Intuitive text input — design without CAD skills.',
        },
        education: {
          title: '3D modeling in education',
          audience: 'For schools & training',
          desc: 'A new learning experience combining programming and 3D modeling.',
        },
      },
      pricingEyebrow: 'Pricing',
      pricingHeadline: 'Simple plans',
      pricingSub: 'Every plan includes a 7-day free trial. Cancel anytime.',
      planTryFree: 'Try free',
      planRecommended: 'Recommended',
      planUnit: '/ month',
      plans: {
        starter: { name: 'Starter', price: '$98' },
        pro: { name: 'Pro', price: '$298' },
        business: { name: 'Business', price: '$980' },
      },
      planFeatureLabels: {
        generations: 'Generations',
        gen2d3d: '2D / 3D generation',
        parametricEdit: 'Parametric editing',
        export: 'Export',
        history: 'History',
        priorityQueue: 'Priority queue',
        teamSeats: 'Team seats',
        support: 'Support',
      },
      planFeatureValues: {
        starterGenerations: '50 / month',
        proGenerations: '300 / month',
        businessGenerations: 'Unlimited',
        starterHistory: '30 days',
        proHistory: 'Unlimited',
        businessHistory: 'Unlimited',
        starterSeats: '1 seat',
        proSeats: '1 seat',
        businessSeats: 'Up to 10',
      },
      finalCtaTitle1: 'Reinvent',
      finalCtaTitle2: 'design.',
      finalCtaSub: 'Hand design over to your AI CAD agent.',
      finalCtaButton: '7-day free trial',
      finalCtaNote: 'No credit card required · cancel anytime',
      footerCopyright: '© 2026 AgentCAD',
      footerTerms: 'Terms',
      footerPrivacy: 'Privacy',
      footerContact: 'Contact',
    },
    contact: {
      backToTop: 'Back to top',
      title: 'Contact',
      description: 'Questions, feedback, bug reports — we welcome all of it.',
      submittedMessage: 'Your email client will open. Please send the email.',
      sendAgain: 'Send again',
      nameLabel: 'Name',
      namePlaceholder: 'Jane Doe',
      emailLabel: 'Email',
      emailPlaceholder: 'jane@example.com',
      messageLabel: 'Message',
      messagePlaceholder: 'Type your message...',
      submit: 'Send',
      mailtoSubject: 'AgentCAD inquiry: {{name}}',
      mailtoBody: 'Name: {{name}}\nEmail: {{email}}\n\n{{message}}',
    },
    privacy: {
      backToTop: 'Back to top',
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: March 15, 2026',
      sections: {
        collect: {
          heading: '1. Information we collect',
          intro: 'In providing this service, we collect the following information.',
          items: [
            'Account information such as email address and password',
            'Project data created within the service',
            'Usage logs (IP address, browser information, access date and time, etc.)',
            'Technical information such as cookies',
          ],
        },
        purpose: {
          heading: '2. Purpose of use',
          items: [
            'Providing, operating, and improving the service',
            'Providing user support',
            'Sending important notifications about the service',
            'Analyzing usage and producing statistics',
            'Detecting and preventing misuse',
          ],
        },
        thirdParty: {
          heading: '3. Sharing with third parties',
          intro: 'We do not share user personal information with third parties except in the following cases.',
          items: [
            'When the user has consented',
            'When required by law',
            "When necessary to protect a person's life, body, or property",
            'Sharing with service providers required to operate the service (Firebase, Google Cloud, etc.)',
          ],
        },
        storage: {
          heading: '4. Data storage',
          body: 'User data is securely stored on Google Cloud Platform. We take appropriate security measures to prevent unauthorized access, leakage, and tampering.',
        },
        cookies: {
          heading: '5. Use of cookies',
          body: 'This service uses cookies to manage authentication state and analyze usage. You can disable cookies in your browser settings, but some features may become unavailable.',
        },
        rights: {
          heading: '6. User rights',
          intro: 'Users have the following rights.',
          items: [
            'Request disclosure, correction, or deletion of personal information',
            'Account deletion',
            'Data export',
          ],
        },
        changes: {
          heading: '7. Changes to this policy',
          body: 'We may update this policy from time to time. We will notify users of significant changes within the service.',
        },
        contact: {
          heading: '8. Contact',
          bodyPrefix: 'For privacy inquiries, please use our ',
          link: 'contact page',
          bodySuffix: '.',
        },
      },
    },
    terms: {
      backToTop: 'Back to top',
      title: 'Terms of Service',
      lastUpdated: 'Last updated: March 15, 2026',
      sections: {
        application: {
          heading: 'Article 1 (Application)',
          body: 'These Terms of Service (the "Terms") set forth the conditions for use of AgentCAD (the "Service"). By using the Service, the user is deemed to have agreed to these Terms.',
        },
        account: {
          heading: 'Article 2 (Account)',
          body: 'The user shall provide accurate and current information when registering an account. Account management is the responsibility of the user, and lending or transferring the account to a third party is prohibited.',
        },
        serviceContent: {
          heading: 'Article 3 (Service content)',
          body: 'The Service is an AI-based CAD tool that generates parametric 2D drawings and 3D models from text input. The contents of the Service are subject to change without notice.',
        },
        prohibited: {
          heading: 'Article 4 (Prohibited acts)',
          items: [
            'Acts that violate laws and regulations',
            'Acts that interfere with the operation of the Service',
            'Acts that disadvantage other users',
            'Reverse engineering, unauthorized access',
            'Generation of illegal content using the Service',
          ],
        },
        ip: {
          heading: 'Article 5 (Intellectual property)',
          body: 'Copyright in content generated by the Service belongs to the user to the extent allowed by applicable law. However, intellectual property rights to the Service itself — including software, design, and trademarks — belong to us.',
        },
        disclaimer: {
          heading: 'Article 6 (Disclaimer)',
          body: 'The Service is provided "as is". We make no warranty as to the accuracy, completeness, or fitness for a particular purpose of AI-generated outputs. We are not liable for damages arising from use of the Service to the maximum extent permitted by law.',
        },
        changes: {
          heading: 'Article 7 (Changes and termination)',
          body: 'We may change or terminate the Service at any time without prior notice. We are not liable for damages caused to the user as a result.',
        },
        law: {
          heading: 'Article 8 (Governing law and jurisdiction)',
          body: 'These Terms are governed by the laws of Japan. Disputes related to the Service shall be subject to the exclusive jurisdiction of the Tokyo District Court as the court of first instance.',
        },
      },
    },
    errorPage: {
      title: 'An error occurred',
      description: "Sorry, an error occurred while loading this page.\nPlease contact us so we can resolve the issue.",
      home: 'Back to home',
    },
  },
  ja: {
    common: {
      home: 'ホーム',
      newProject: '新規作成',
      projects: '作品',
      collapse: '折りたたむ',
      expandSidebar: 'サイドバーを展開',
      logout: 'ログアウト',
      language: '言語',
      desktopOnly: 'デスクトップブラウザでアクセスしてください',
      untitledProject: '無題の作品',
      thinking: '思考',
      checkpointSaved: 'チェックポイント保存',
      parts: 'パーツ',
      joints: '接合',
      unsetCategory: '未設定',
      brand: 'AgentCAD',
      units: { piece: '個', count: '件', shelves: '段', slats: '本', floors: '階', buildings: '棟', teeth: '枚' },
      chat: {
        messagePlaceholder: 'AgentCADと一緒に改良を続けましょう...',
        interruptRunning: 'エージェント実行中 — 途中で方向修正できます',
        interruptPlaceholder: '例: 木材を胡桃材に / 脚を円柱に...',
        sentPrefix: '✓ 送信: {{text}}',
        modifyQuestion: 'どう修正しますか？',
        modifyPlaceholder: '例: 脚を細くして、高さを700mmに...',
        send: '送信',
        back: '戻る',
        continue: '続ける',
        modify: '修正',
        turboTitle: '次回以降は自動で続行',
        turboMode: 'ターボモード',
        done: '完了',
        freeAnswerPlaceholder: '自由回答...',
        fixWithAI: 'AIで修正',
        edit: '編集',
        copyContent: '内容をコピー',
        continueDefault: '続けてください',
        errorOccurred: 'エラーが発生しました',
        retry: '再試行',
        defaultObjectTitle: 'AgentCADオブジェクト',
        chatTitleFallback: 'チャット',
        suggestionsHint: '選択肢から回答できます',
        suggestionsSubmit: '回答を送信',
        welcomePlaceholder: '今日はAgentCADで何を作りますか？',
        imageUploadFailed: '画像のアップロードに失敗しました',
        renderUploadFailed: 'レンダーのアップロードに失敗しました',
        modelProcessingError: '3Dモデルの処理エラー',
        singleModelTitle: '3Dモデルは一度に1つまで',
        singleModelDesc: '別のモデルを追加する前に、現在のモデルを削除してください。',
        multipleModelsTitle: '複数の3Dモデル',
        multipleModelsDesc: '最初のSTLファイルのみ追加されました。',
        imageTooSmallTitle: '画像が小さすぎます',
        imageTooSmallDesc: '256x256ピクセル未満の画像は追加されませんでした。',
        imageTooLargeTitle: '画像が大きすぎます',
        imageTooLargeDesc: '100MBを超える画像は追加されませんでした。',
        invalidImageTitle: '無効な画像形式',
        invalidImageDesc: '有効な画像形式ではない画像は追加されませんでした。jpeg、png、またはwebp形式である必要があります。',
        addImageHere: 'ここに画像を追加',
        dropImagesOrModels: '画像や3Dモデルをここにドロップ',
        modelViewAlt: '3Dモデルビュー',
        imageAlt: '画像',
        imageNotFound: '画像が見つかりません',
        messagesCount: '{{count}}件のメッセージ',
      },
      toolNames: {
        add_part: 'パーツを追加',
        modify_part: 'パーツを変更',
        remove_part: 'パーツを削除',
        add_joint: '接合を追加',
        modify_joint: '接合を変更',
        remove_joint: '接合を削除',
        set_parameter: 'パラメータを設定',
        set_view: '視点を設定',
        validate: '検証',
        render: 'レンダリング',
        ask_user: '質問',
      },
      toast: { error: 'エラー', success: '成功' },
      dialog: { cancel: 'キャンセル', delete: '削除', save: '保存', confirm: '確定' },
      history: {
        title: '過去の作品',
        list: 'リスト',
        visual: 'ビジュアル',
        searchPlaceholder: '作品を検索...',
        noMatchTitle: '一致する会話が見つかりません',
        noMatchHint: '別の検索語を試してください',
        emptyTitle: 'まだ会話がありません',
        emptyHint: '新しいチャットを開始してCADを作成しましょう',
        startNewChat: '新しいチャットを開始',
        newProjectAria: '新しい作品を作成',
        deleteConversationTitle: '会話を削除',
        deleteConversationDesc: 'この会話を削除してもよろしいですか？この操作は元に戻せません。',
        deleteProjectTitle: '作品を削除',
        deleteProjectDesc: 'この作品を削除してもよろしいですか？この操作は元に戻せません。',
        renameTitle: '作品の名前を変更',
        renameDesc: 'この作品の新しい名前を入力してください。',
        renamePlaceholder: '会話の名前',
        rename: '名前を変更',
        compiling: 'コンパイル中...',
        loadFailed: '会話の読み込みに失敗しました',
        deleted: '会話を削除しました',
        deleteFailed: '会話の削除に失敗しました',
        renamed: '会話の名前を変更しました',
        renameFailed: '会話の名前変更に失敗しました',
        titleEmpty: 'タイトルを空にすることはできません',
        dateFormat: 'yyyy年M月d日',
      },
      editor: {
        notFound: '会話が見つかりません',
        chatVertical: 'チャット',
        outputVertical: '出力',
        expandChat: 'チャットパネルを展開',
        collapseChat: 'チャットパネルを折りたたむ',
        expandOutput: '出力パネルを展開',
        collapseOutput: '出力パネルを折りたたむ',
      },
      viewer: {
        generating: 'モデルを生成中',
        empty: 'チャットでプロンプトを送信してCADモデルを生成',
        compiling: 'コンパイル中...',
        compileError: 'コンパイルエラー',
        compileErrorDesc: 'AgentCADがコンパイル中にエラーを検出しました',
        fixWithAI: 'AIで修正',
        view3D: '3D表示',
        view2D: '2D図面',
        nextImage: '次の画像',
        previousImage: '前の画像',
        rendering: 'レンダリング中',
        loadingModel: 'モデルを読み込み中',
        perspective: '透視投影ビュー',
        orthographic: '正投影ビュー',
        gizmo: { right: '右', left: '左', top: '上', bottom: '下', front: '前', back: '後' },
        surfaceImages: {
          add: '表面画像を追加',
          select: '画像{{index}}を選択',
          remove: '表面画像を削除',
          scale: '拡大縮小',
          rotate: '回転',
          opacity: '不透明度',
        },
        feedback: {
          improve3D: ['壁を厚くして', '角を丸くして', 'もっと詳細に', 'サイズを大きく'],
          improve2D: ['寸法を追加して', '断面図を追加', 'ビューを増やして', '注釈を改善して'],
          improve3DPrefix: '【3Dモデルの改善】',
          improve2DPrefix: '【2D図面の改善】',
          placeholder3D: 'モデルの改善点を入力...',
          placeholder2D: '図面の改善点を入力...',
          improveButton: 'AIで改善',
        },
      },
      parameter: {
        color: 'カラー',
        controls: 'パラメータ',
        noEditable: '編集できるパラメータはまだありません。',
        reset: 'パラメータをリセット',
        openColorPicker: 'カラーピッカーを開く',
        download: 'ダウンロード',
        downloadAria: '{{format}}をダウンロード',
      },
      output: {
        title: '出力',
        appearance: '外観',
        export: 'エクスポート',
        formatGroupAria: 'エクスポート形式',
        exportEmpty: 'まだエクスポートできるものがありません。モデルを生成してください。',
        exportHint: 'このモデルの現在の{{format}}を保存します。',
      },
      spec: {
        visionEval: 'ビジョン評価',
        partsLabel: 'パーツ',
        jointsLabel: '接合',
        specPrefix: 'Spec · ',
        specPartsSuffix: ' パーツ / ',
        specJointsSuffix: ' 接合',
        visualNotes: 'ビジュアルメモ',
        noStructured: 'このメッセージには構造化されたSpecがありません。',
        noJoints: '(なし)',
        noParameters: '(なし)',
        noMaterials: '(なし)',
        treeParts: 'パーツ',
        treeJoints: '接合',
        treeParameters: 'パラメータ',
        treeMaterials: '材料',
        opening: '開口',
        viewFront: '正面',
        viewTop: '平面',
        viewRight: '右側',
        viewIso: 'アイソメ',
        viewLayout: '配置',
      },
      drawing: {
        bomGenerate: '部品表生成',
        bomTitle: '部品表',
        bomParts: '部品',
        bomNo: 'No.',
        bomName: '名称',
        bomSpec: '仕様',
        bomQty: '数量',
        bomMaterial: '材質',
        bomDimensions: '寸法',
        bomNotes: '備考',
        partName: '部品名',
        addRow: '行を追加',
        confirmAndContinue: '確定',
        customInput: 'カスタム入力...',
        attachments: '付属品',
        weight: '質量：',
        untitled: '無題',
        dateLabel: '年 月 日',
        scaleLabel: '縮　尺',
        itemNameLabel: '品　名',
        drawingNumberLabel: '図面番号',
        draftedBy: '作図',
        checkedBy: '確認',
        approvedBy: '承認',
        manufacturingDrawing: '製造図面',
        regenerate: '再生成',
        generating: '生成中…',
        generate: '生成',
        strictModeError: '⚠ 厳格モードで {{count}} 個のエラーが見つかりました',
        bomReview: '部品表を確認・編集',
        fitToView: '表示にフィット',
        exportSVG: 'SVGをエクスポート',
        exportPDF: 'PDFをエクスポート',
        statusQueued: '待機中',
        statusRunning: '実行中',
        statusReady: '準備完了',
        statusFailed: '失敗',
        cutListPartsTable: '部品表生成',
        cutListHeaderNumber: '#',
        cutListHeaderPart: '部品',
        cutListHeaderKind: '種類',
        cutListHeaderWHT: 'W×H×T',
        cutListHeaderMaterial: '材質',
        cutListHeaderQty: '数量',
        cutListFlattenSkipped: '展開スキップ: ',
      },
      services: {
        loginRequired: 'ログインが必要です',
        conversationIdRequired: '会話IDが必要です',
        conversationNotFound: '会話が見つかりません',
        chatApiError: 'チャットAPIエラー',
        chatNoBody: '応答本文を取得できませんでした',
        chatNoResponse: 'チャット関数から応答を受信できませんでした',
        errorPrefix: 'エラー: ',
        intendedUseRequired: '用途を入力してください',
        cannotUpdateConversation: '会話を更新できません',
        intakeMismatch: 'category と subType が details と一致しません',
        untitledDesign: '無題のデザイン',
        unitMm: 'mm',
        webglUnavailable: 'WebGLが利用できません。WebGLに対応したブラウザをご利用ください。',
        noOutput: '出力がありません',
        drawingJobNotFound: '図面ジョブが見つかりません',
        noSectionOutput: '断面コンパイルからの出力がありません',
        noProjectionOutput: '投影コンパイルからの出力がありません',
        noFileInArtifact: 'アーティファクトに{{ext}}ファイルがありません',
      },
      meshChat: {
        intro:
          'メッシュモデルは彫刻的に生成され、パラメトリックではありません。このモードでは編集できません。別の結果が欲しい場合は再生成するか、テキストで説明してパラメトリックモデル用の新しい会話を開始してください。',
        regenerateButton: '再生成',
        regenerateFailedTitle: '再生成を開始できませんでした',
        downloadAria: '{{format}}をダウンロード',
        downloadFailedTitle: '{{format}}をダウンロードできませんでした',
      },
      meshError: {
        messageFailed: 'メッシュ生成に失敗しました。別の画像をお試しください。',
        messageBanned:
          'メッシュサービスが画像を拒否しました。別の画像をお試しください。',
        messageExpired: 'メッシュ生成が完了しませんでした。再度お試しください。',
        messageCancelled: 'メッシュ生成が完了しませんでした。再度お試しください。',
        messageUnknown: 'メッシュ生成が完了しませんでした。再度お試しください。',
        messageGeneric: 'メッシュ生成に失敗しました。',
        retryButton: '再試行',
        dismissButton: '閉じる',
        codeSuffix: '(コード {{code}})',
      },
      meshUnavailable: {
        message:
          'メッシュは現在利用できません。チャットパネルから再生成してください。',
        reloadButton: '再読み込み',
      },
    },
    prompt: {
      morning: 'おはようございます',
      afternoon: 'こんにちは',
      evening: 'こんばんは',
      subtitlePrefix: 'フォームに記入して',
      subtitleDrawing: '2D図面',
      subtitleMiddle: 'と',
      subtitleModel: '3Dモデル',
      quickExamples: 'クイック例:',
      examples: {
        gear: { label: '歯車', intendedUse: 'パラメトリックな平歯車' },
        vase: { label: '花瓶', intendedUse: '有機的な形状の花瓶' },
        architectureModel: { label: '建築模型', intendedUse: 'モダンな住宅の建築模型' },
        bracket: { label: 'ブラケット', intendedUse: '取り付け用Lブラケット' },
        cabinet: { label: '食器棚', intendedUse: '2扉の壁付け食器棚' },
        diningTable: { label: 'ダイニングテーブル', intendedUse: '6人用のダイニングテーブル' },
      },
      loginRequiredTitle: 'エラー',
      loginRequiredDescription: 'ログインが必要です',
      generationFailed: '生成に失敗しました',
      newConversationTitle: '新しい会話',
      textStart: {
        headline: 'または、つくりたいものを説明してください',
        subline:
          '一文か二文で、サイズ・素材・用途がわかれば書いてください。説明から作り始めます。',
        placeholder:
          '例: 1200×800×720mmのオーク材ダイニングテーブル、四角テーパー脚。',
        buildButton: 'これでつくる',
        buildingButton: '開始中…',
      },
      imageStart: {
        headline: '参考画像はありますか？',
        subline:
          '写真をドロップすると3Dメッシュを生成します（パラメトリック編集不可）。',
        starting: 'メッシュ生成を開始中…',
        pickButton: '画像を選択',
        invalidTypeTitle: '対応していない画像形式',
        invalidTypeDescription:
          'JPEG、PNG、WEBP形式のファイルをアップロードしてください。',
        tooLargeTitle: '画像サイズが大きすぎます',
        tooLargeDescription: '最大4MBです。サイズを変更して再試行してください。',
        kickoffFailedTitle: 'メッシュ生成を開始できませんでした',
        kickoffFailedWhile: '({{step}}中)',
        confirmHeadline: '生成を開始しますか？',
        confirmButton: '3Dメッシュを生成',
        clearButton: 'クリア',
        previewAlt: 'アップロードした参考画像のプレビュー',
        uploading: '画像をアップロード中…',
        uploadFailedTitle: '画像をアップロードできませんでした',
        steps: {
          createConversation: '下書き会話の作成',
          uploadImage: '画像のアップロード',
          startMesh: 'メッシュ生成の開始',
        },
      },
      meshModeToggle: {
        label: 'メッシュモード（画像から3D）',
        imageHint: 'メッシュモードをオンにして画像をアップロードしてください。',
        textHint: 'メッシュモードをオフにしてテキスト入力をご利用ください。',
      },
    },
    intake: {
      category: '1. カテゴリ',
      subType: '2. 種類',
      quality: '3. 品質レベル',
      core: '4. 基本情報',
      details: '5. 詳細',
      drawing: '6. 図面の希望',
      freeform: '7. 特記事項（任意）',
      inputError: '入力エラー',
      generate: '生成',
      intent: '作りたいもの',
      intentPlaceholder: '任意: 6人用ダイニングテーブル、棚用ブラケット、電子工作ケース...',
      advanced: '詳細設定',
      hideAdvanced: '詳細を閉じる',
      coreLabels: {
        intendedUse: '用途',
        intendedUsePlaceholder: '家族6人で食事に使うダイニングテーブル',
        size: '寸法',
        material: '素材',
        fastenerPreference: '接合方式（任意）',
      },
      dimensions: { length: '長', width: '幅', height: '高' },
      freeformPlaceholder: 'フォームに収まらない要件があればここに（最大1000字）',
      categories: {
        furniture: { label: '家具・木工', hint: 'テーブル・椅子・棚' },
        mechanical: { label: '機械部品', hint: 'ブラケット・歯車・筐体' },
        architecture: { label: '建築模型', hint: '住宅・建物・都市' },
        printing: { label: '3Dプリント', hint: '小物・スタンド・ベース' },
      },
      subTypes: {
        table: 'テーブル', chair: '椅子', shelf: '棚', bed: 'ベッド', desk: 'デスク', cabinet: 'キャビネット',
        bracket: 'ブラケット', housing: '筐体', gear: '歯車', shaft: 'シャフト',
        fastener_part: 'ファスナー', fixture: '治具',
        building: '建物', interior_room: '内装', site: '敷地', facade_detail: '外観詳細',
        decorative: '装飾', functional: '機能部品', replacement: '交換部品',
        miniature: 'ミニチュア', enclosure: 'ケース',
      },
      qualityLevels: {
        draft: { label: '下書き', hint: 'シンプルな形状で素早く' },
        standard: { label: '標準', hint: '通常の細部' },
        manufacturing: { label: '製造', hint: '寸法・公差・部品リスト含む' },
        extreme: { label: '極限', hint: 'ネジ・ジョイント・全細部' },
      },
      drawingPrefs: {
        scope: '範囲',
        section: '断面',
        tolerance: '公差クラス',
        holeTable: '穴表',
        bom: '部品表',
        jisStrict: 'JIS厳格',
        scopes: { assembly_only: '組立図のみ', parts_only: '部品図のみ', both: '両方' },
        sections: { auto: '自動', none: 'なし' },
        tolerances: { loose: 'ゆるい', standard: '標準', tight: 'タイト', precision: '高精度' },
      },
      optionLabels: jaOptionLabels,
      fieldLabels: jaFieldLabels,
      units: {
        '段': '段',
        '本': '本',
        '個': '個',
        '枚': '枚',
        '階': '階',
        '棟': '棟',
      },
    },
    auth: {
      heroTitleLine1: 'AIとの共創で、',
      heroTitleLine2: 'アイデアを形に。',
      heroSubtitle: 'テキストから2D図面・3Dモデルを瞬時に生成するCADエージェント。',
      signupTitle: 'アカウント作成',
      loginTitle: 'おかえりなさい',
      signupSubtitle: '7日間の無料トライアルを開始',
      loginSubtitle: 'アカウントにログイン',
      emailLabel: 'メールアドレス',
      emailPlaceholder: 'you@example.com',
      passwordLabel: 'パスワード',
      passwordPlaceholder: '6文字以上',
      submitSignup: 'アカウントを作成',
      submitLogin: 'サインイン',
      submitting: '...',
      hasAccount: 'すでにアカウントをお持ちですか？',
      noAccount: 'アカウントをお持ちでない方は',
      toggleToLogin: 'サインイン',
      toggleToSignup: 'アカウント作成',
      errors: {
        invalidCredential: 'メールアドレスまたはパスワードが正しくありません',
        emailInUse: 'このメールアドレスは既に使用されています',
        weakPassword: 'パスワードは6文字以上にしてください',
        tooManyRequests: 'リクエストが多すぎます。しばらくしてからもう一度お試しください',
        unknown: 'エラーが発生しました。もう一度お試しください',
      },
    },
    landing: {
      navLogin: 'ログイン',
      navStartFree: '無料で始める',
      heroTitle1: '言葉だけで、',
      heroTitle2: '設計する。',
      heroSubtitle: 'テキストからパラメトリックな2D図面・3Dモデルを瞬時に生成。AgentCADが、設計の常識を変える。',
      heroCta: '7日間無料トライアル',
      heroCtaNote: 'クレジットカード不要',
      statTimeSaved: '設計時間を短縮',
      statSetup: 'セットアップ時間',
      statSetupSuffix: '秒',
      statCost: '初期費用',
      statCostSuffix: '円',
      statTimeSuffix: '%',
      featuresEyebrow: 'Features',
      featuresHeadline: 'すべてが、テキストから始まる。',
      features: {
        textTo2d3d: {
          title: 'テキストから2D・3D生成',
          tag: '頭の中のイメージを、そのまま形に。',
          desc: 'テキストで説明するだけで、AIがOpenSCADベースのパラメトリックモデルと2D図面を自動生成。',
        },
        realtime: {
          title: 'リアルタイム編集',
          tag: '微調整も、一瞬で。',
          desc: 'スライダー操作でサイズ・形状・角度をリアルタイムに変更。再生成の待ち時間ゼロ。',
        },
        multiAngle: {
          title: 'マルチアングル表示',
          tag: 'あらゆる角度から確認。',
          desc: '360°自由回転ビュー。あらゆる角度からリアルタイムに確認。',
        },
        export: {
          title: 'エクスポート',
          tag: 'そのまま納品。',
          desc: 'PDF・STL形式でワンクリックエクスポート。',
        },
      },
      stepsEyebrow: 'How it works',
      stepsHeadline: '3ステップで完了。',
      steps: {
        describe: { title: 'テキストで説明する', desc: '作りたいものを日本語で入力するだけ。専門知識は不要。' },
        generate: { title: 'AIが自動生成', desc: '2D図面と3Dモデルを瞬時に作成。パラメトリック対応。' },
        editExport: { title: '編集・エクスポート', desc: 'パラメータ調整して完成品をダウンロード。' },
      },
      useCasesEyebrow: 'Use Cases',
      useCasesHeadline: 'AgentCADが選ばれる理由',
      useCasesSub: 'あなた専用のCADエージェントが、24時間稼働。',
      useCases: {
        prototype: {
          title: 'プロトタイピングを10倍速く',
          audience: '製品開発チーム向け',
          desc: 'アイデアをすぐに3Dモデル化。試作サイクルを劇的に短縮。',
        },
        nonExpert: {
          title: 'CADの専門知識がなくても',
          audience: 'デザイナー・企画職向け',
          desc: '直感的なテキスト入力で、CADスキル不要で設計を実現。',
        },
        education: {
          title: '教育現場での3Dモデリング',
          audience: '学校・研修向け',
          desc: 'プログラミング教育と3Dモデリングを組み合わせた新しい学習体験。',
        },
      },
      pricingEyebrow: 'Pricing',
      pricingHeadline: 'シンプルな料金プラン',
      pricingSub: 'すべてのプランに7日間の無料トライアル付き。いつでもキャンセル可能。',
      planTryFree: '無料で試す',
      planRecommended: 'おすすめ',
      planUnit: '/月',
      plans: {
        starter: { name: 'スターター', price: '¥9,800' },
        pro: { name: 'プロ', price: '¥29,800' },
        business: { name: 'ビジネス', price: '¥98,000' },
      },
      planFeatureLabels: {
        generations: '生成回数',
        gen2d3d: '2D/3D生成',
        parametricEdit: 'パラメトリック編集',
        export: 'エクスポート',
        history: '履歴保存',
        priorityQueue: '優先キュー',
        teamSeats: 'チーム人数',
        support: 'サポート',
      },
      planFeatureValues: {
        starterGenerations: '50回/月',
        proGenerations: '300回/月',
        businessGenerations: '無制限',
        starterHistory: '30日',
        proHistory: '無制限',
        businessHistory: '無制限',
        starterSeats: '1名',
        proSeats: '1名',
        businessSeats: '10名まで',
      },
      finalCtaTitle1: '設計を、',
      finalCtaTitle2: '再発明しよう。',
      finalCtaSub: 'AIのCADエージェントに、設計を任せよう。',
      finalCtaButton: '7日間無料トライアル',
      finalCtaNote: 'クレジットカード不要・いつでもキャンセル可能',
      footerCopyright: '© 2026 AgentCAD',
      footerTerms: '利用規約',
      footerPrivacy: 'プライバシーポリシー',
      footerContact: 'お問い合わせ',
    },
    contact: {
      backToTop: 'トップに戻る',
      title: 'お問い合わせ',
      description: 'ご質問、ご要望、不具合報告など、お気軽にお問い合わせください。',
      submittedMessage: 'メールクライアントが開きます。送信をお願いいたします。',
      sendAgain: 'もう一度送信する',
      nameLabel: 'お名前',
      namePlaceholder: '山田 太郎',
      emailLabel: 'メールアドレス',
      emailPlaceholder: 'taro@example.com',
      messageLabel: 'お問い合わせ内容',
      messagePlaceholder: 'お問い合わせ内容をご記入ください...',
      submit: '送信する',
      mailtoSubject: 'AgentCADへのお問い合わせ: {{name}}',
      mailtoBody: '名前: {{name}}\nメール: {{email}}\n\n{{message}}',
    },
    privacy: {
      backToTop: 'トップに戻る',
      title: 'プライバシーポリシー',
      lastUpdated: '最終更新日: 2026年3月15日',
      sections: {
        collect: {
          heading: '1. 収集する情報',
          intro: '当社は、本サービスの提供にあたり、以下の情報を収集します。',
          items: [
            'メールアドレス、パスワード等のアカウント情報',
            '本サービスで作成されたプロジェクトデータ',
            '利用状況に関するログデータ（IPアドレス、ブラウザ情報、アクセス日時等）',
            'Cookie等の技術的情報',
          ],
        },
        purpose: {
          heading: '2. 情報の利用目的',
          items: [
            '本サービスの提供・運営・改善',
            'ユーザーサポートの提供',
            'サービスに関する重要な通知の送信',
            '利用状況の分析・統計処理',
            '不正利用の検知・防止',
          ],
        },
        thirdParty: {
          heading: '3. 第三者への提供',
          intro: '当社は、以下の場合を除き、ユーザーの個人情報を第三者に提供しません。',
          items: [
            'ユーザーの同意がある場合',
            '法令に基づく場合',
            '人の生命・身体・財産の保護に必要な場合',
            'サービス提供に必要な業務委託先への提供（Firebase、Google Cloud等）',
          ],
        },
        storage: {
          heading: '4. データの保管',
          body: 'ユーザーデータはGoogle Cloud Platform上に安全に保管されます。適切なセキュリティ対策を講じ、不正アクセス・漏洩・改ざんの防止に努めます。',
        },
        cookies: {
          heading: '5. Cookieの使用',
          body: '本サービスでは、認証状態の管理および利用状況の分析のためにCookieを使用します。ブラウザの設定によりCookieを無効にすることができますが、一部の機能が利用できなくなる場合があります。',
        },
        rights: {
          heading: '6. ユーザーの権利',
          intro: 'ユーザーは以下の権利を有します。',
          items: [
            '個人情報の開示・訂正・削除の請求',
            'アカウントの削除',
            'データのエクスポート',
          ],
        },
        changes: {
          heading: '7. ポリシーの変更',
          body: '当社は、本ポリシーを随時変更することがあります。重要な変更がある場合は、本サービス上で通知します。',
        },
        contact: {
          heading: '8. お問い合わせ',
          bodyPrefix: 'プライバシーに関するお問い合わせは、',
          link: 'お問い合わせページ',
          bodySuffix: 'よりご連絡ください。',
        },
      },
    },
    terms: {
      backToTop: 'トップに戻る',
      title: '利用規約',
      lastUpdated: '最終更新日: 2026年3月15日',
      sections: {
        application: {
          heading: '第1条（適用）',
          body: '本利用規約（以下「本規約」）は、AgentCAD（以下「本サービス」）の利用に関する条件を定めるものです。ユーザーは本サービスを利用することにより、本規約に同意したものとみなされます。',
        },
        account: {
          heading: '第2条（アカウント）',
          body: 'ユーザーは、正確かつ最新の情報を提供してアカウントを登録する必要があります。アカウントの管理はユーザー自身の責任とし、第三者への貸与・譲渡は禁止します。',
        },
        serviceContent: {
          heading: '第3条（サービス内容）',
          body: '本サービスは、テキスト入力からパラメトリックな2D図面・3Dモデルを生成するAIベースのCADツールです。サービスの内容は予告なく変更される場合があります。',
        },
        prohibited: {
          heading: '第4条（禁止事項）',
          items: [
            '法令に違反する行為',
            '本サービスの運営を妨害する行為',
            '他のユーザーに不利益を与える行為',
            'リバースエンジニアリング、不正アクセス',
            '本サービスを利用した違法なコンテンツの生成',
          ],
        },
        ip: {
          heading: '第5条（知的財産権）',
          body: '本サービスにより生成されたコンテンツの著作権は、適用法令の範囲内でユーザーに帰属します。ただし、本サービス自体のソフトウェア、デザイン、商標等の知的財産権は当社に帰属します。',
        },
        disclaimer: {
          heading: '第6条（免責事項）',
          body: '本サービスは「現状有姿」で提供されます。AI生成物の正確性、完全性、特定目的への適合性について、当社は一切の保証をいたしません。本サービスの利用により生じた損害について、当社は法令で許容される最大限の範囲で責任を負いません。',
        },
        changes: {
          heading: '第7条（サービスの変更・終了）',
          body: '当社は、事前の通知なく本サービスの内容を変更、または提供を終了することがあります。これによりユーザーに生じた損害について、当社は責任を負いません。',
        },
        law: {
          heading: '第8条（準拠法・管轄）',
          body: '本規約は日本法に準拠し、本サービスに関する紛争については東京地方裁判所を第一審の専属的合意管轄裁判所とします。',
        },
      },
    },
    errorPage: {
      title: 'エラーが発生しました',
      description: '申し訳ありませんが、このページの読み込み中にエラーが発生しました。\n問題を解決するためにお問い合わせください。',
      home: 'ホームへ戻る',
    },
  },
  ar: {
    common: {
      home: 'الرئيسية',
      newProject: 'مشروع جديد',
      projects: 'المشاريع',
      collapse: 'طي الشريط',
      expandSidebar: 'توسيع الشريط',
      logout: 'تسجيل الخروج',
      language: 'اللغة',
      desktopOnly: 'يرجى فتح AgentCAD من متصفح سطح المكتب.',
      untitledProject: 'مشروع بلا عنوان',
      thinking: 'التفكير',
      checkpointSaved: 'تم حفظ نقطة تحقق',
      parts: 'أجزاء',
      joints: 'وصلات',
      unsetCategory: 'غير محدد',
      brand: 'AgentCAD',
      units: { piece: '', count: '', shelves: 'رف', slats: 'شريحة', floors: 'طابق', buildings: 'مبنى', teeth: 'سن' },
      chat: {
        messagePlaceholder: 'تابع التحسين مع AgentCAD...',
        interruptRunning: 'الوكيل يعمل، ويمكنك توجيهه أثناء التنفيذ',
        interruptPlaceholder: 'مثال: غيّر الخشب إلى جوز / اجعل الأرجل أسطوانية...',
        sentPrefix: 'تم الإرسال: {{text}}',
        modifyQuestion: 'ما التعديل المطلوب؟',
        modifyPlaceholder: 'مثال: اجعل الأرجل أنحف واضبط الارتفاع إلى 700mm...',
        send: 'إرسال',
        back: 'رجوع',
        continue: 'متابعة',
        modify: 'تعديل',
        turboTitle: 'المتابعة تلقائيا من الآن',
        turboMode: 'وضع التوربو',
        done: 'تم',
        freeAnswerPlaceholder: 'إجابة حرة...',
        fixWithAI: 'إصلاح بالذكاء الاصطناعي',
        edit: 'تعديل',
        copyContent: 'نسخ المحتوى',
        continueDefault: 'تابع من فضلك.',
        errorOccurred: 'حدث خطأ',
        retry: 'إعادة المحاولة',
        defaultObjectTitle: 'كائن AgentCAD',
        chatTitleFallback: 'محادثة',
        suggestionsHint: 'يمكنك الإجابة من الخيارات',
        suggestionsSubmit: 'إرسال الإجابة',
        welcomePlaceholder: 'ماذا ستبني مع AgentCAD اليوم؟',
        imageUploadFailed: 'فشل رفع الصورة',
        renderUploadFailed: 'فشل رفع التصيير',
        modelProcessingError: 'خطأ في معالجة النموذج ثلاثي الأبعاد',
        singleModelTitle: 'نموذج ثلاثي الأبعاد واحد فقط في كل مرة',
        singleModelDesc: 'احذف النموذج الحالي قبل إضافة نموذج آخر.',
        multipleModelsTitle: 'نماذج متعددة',
        multipleModelsDesc: 'تمت إضافة أول ملف STL فقط.',
        imageTooSmallTitle: 'الصورة صغيرة جدا',
        imageTooSmallDesc: 'لم تتم إضافة الصور الأصغر من 256×256 بكسل.',
        imageTooLargeTitle: 'الصورة كبيرة جدا',
        imageTooLargeDesc: 'لم تتم إضافة الصور الأكبر من 100MB.',
        invalidImageTitle: 'صيغة صورة غير صالحة',
        invalidImageDesc: 'لم تتم إضافة الصور بصيغ غير صالحة. يجب أن تكون jpeg أو png أو webp.',
        addImageHere: 'أضف صورة هنا',
        dropImagesOrModels: 'أفلت الصور أو النماذج ثلاثية الأبعاد هنا',
        modelViewAlt: 'عرض النموذج ثلاثي الأبعاد',
        imageAlt: 'صورة',
        imageNotFound: 'الصورة غير موجودة',
        messagesCount: '{{count}} رسالة',
      },
      toolNames: {
        add_part: 'إضافة جزء',
        modify_part: 'تعديل جزء',
        remove_part: 'حذف جزء',
        add_joint: 'إضافة وصلة',
        modify_joint: 'تعديل وصلة',
        remove_joint: 'حذف وصلة',
        set_parameter: 'ضبط معامل',
        set_view: 'ضبط العرض',
        validate: 'تحقق',
        render: 'تصيير',
        ask_user: 'سؤال',
      },
      toast: { error: 'خطأ', success: 'تم' },
      dialog: { cancel: 'إلغاء', delete: 'حذف', save: 'حفظ', confirm: 'تأكيد' },
      history: {
        title: 'المشاريع السابقة',
        list: 'قائمة',
        visual: 'مرئي',
        searchPlaceholder: 'ابحث في المشاريع...',
        noMatchTitle: 'لا توجد محادثات مطابقة',
        noMatchHint: 'جرب كلمة بحث أخرى',
        emptyTitle: 'لا توجد محادثات بعد',
        emptyHint: 'ابدأ محادثة جديدة لإنشاء CAD',
        startNewChat: 'بدء محادثة جديدة',
        newProjectAria: 'إنشاء مشروع جديد',
        deleteConversationTitle: 'حذف المحادثة',
        deleteConversationDesc: 'هل أنت متأكد من حذف هذه المحادثة؟ لا يمكن التراجع.',
        deleteProjectTitle: 'حذف المشروع',
        deleteProjectDesc: 'هل أنت متأكد من حذف هذا المشروع؟ لا يمكن التراجع.',
        renameTitle: 'إعادة تسمية المشروع',
        renameDesc: 'أدخل اسما جديدا لهذا المشروع.',
        renamePlaceholder: 'اسم المحادثة',
        rename: 'إعادة تسمية',
        compiling: 'جارٍ الترجمة...',
        loadFailed: 'فشل تحميل المحادثات',
        deleted: 'تم حذف المحادثة',
        deleteFailed: 'فشل حذف المحادثة',
        renamed: 'تمت إعادة تسمية المحادثة',
        renameFailed: 'فشل تغيير اسم المحادثة',
        titleEmpty: 'لا يمكن أن يكون العنوان فارغا',
        dateFormat: 'd MMMM yyyy',
      },
      editor: {
        notFound: 'المحادثة غير موجودة',
        chatVertical: 'محادثة',
        outputVertical: 'الإخراج',
        expandChat: 'توسيع لوحة المحادثة',
        collapseChat: 'طي لوحة المحادثة',
        expandOutput: 'توسيع لوحة الإخراج',
        collapseOutput: 'طي لوحة الإخراج',
      },
      viewer: {
        generating: 'يتم إنشاء النموذج',
        empty: 'أرسل طلبا في المحادثة لإنشاء نموذج CAD',
        compiling: 'جارٍ الترجمة...',
        compileError: 'خطأ في الترجمة',
        compileErrorDesc: 'اكتشف AgentCAD خطأ أثناء الترجمة',
        fixWithAI: 'إصلاح بالذكاء الاصطناعي',
        view3D: 'عرض ثلاثي الأبعاد',
        view2D: 'رسم ثنائي الأبعاد',
        nextImage: 'الصورة التالية',
        previousImage: 'الصورة السابقة',
        rendering: 'جارٍ التصيير',
        loadingModel: 'جارٍ تحميل النموذج',
        perspective: 'منظور',
        orthographic: 'إسقاط متعامد',
        gizmo: { right: 'يم', left: 'يس', top: 'فوق', bottom: 'تحت', front: 'أم', back: 'خل' },
        surfaceImages: {
          add: 'إضافة صورة سطح',
          select: 'اختيار الصورة {{index}}',
          remove: 'إزالة صورة السطح',
          scale: 'الحجم',
          rotate: 'التدوير',
          opacity: 'الشفافية',
        },
        feedback: {
          improve3D: ['زِد سماكة الجدار', 'تدوير الزوايا', 'مزيد من التفاصيل', 'كبّر الحجم'],
          improve2D: ['أضف الأبعاد', 'أضف مقطعا', 'أضف مزيدا من المناظير', 'حسّن التعليقات'],
          improve3DPrefix: '【تحسين النموذج ثلاثي الأبعاد】',
          improve2DPrefix: '【تحسين الرسم ثنائي الأبعاد】',
          placeholder3D: 'اكتب تحسينات النموذج...',
          placeholder2D: 'اكتب تحسينات الرسم...',
          improveButton: 'تحسين بالذكاء الاصطناعي',
        },
      },
      parameter: {
        color: 'لون',
        controls: 'معاملات',
        noEditable: 'لا توجد معاملات قابلة للتحرير بعد.',
        reset: 'إعادة ضبط المعامل',
        openColorPicker: 'افتح منتقي الألوان',
        download: 'تحميل',
        downloadAria: 'تحميل {{format}}',
      },
      output: {
        title: 'الإخراج',
        appearance: 'المظهر',
        export: 'تصدير',
        formatGroupAria: 'تنسيق التصدير',
        exportEmpty: 'لا يوجد شيء للتصدير بعد — قم بإنشاء نموذج أولاً.',
        exportHint: 'يحفظ {{format}} الحالي لهذا النموذج.',
      },
      spec: {
        visionEval: 'تقييم بصري',
        partsLabel: 'أجزاء',
        jointsLabel: 'وصلات',
        specPrefix: 'مواصفات · ',
        specPartsSuffix: ' أجزاء / ',
        specJointsSuffix: ' وصلات',
        visualNotes: 'ملاحظات بصرية',
        noStructured: 'لا توجد مواصفات مهيكلة لهذه الرسالة.',
        noJoints: '(بدون)',
        noParameters: '(بدون)',
        noMaterials: '(بدون)',
        treeParts: 'أجزاء',
        treeJoints: 'وصلات',
        treeParameters: 'معاملات',
        treeMaterials: 'مواد',
        opening: 'فتحة',
        viewFront: 'الأمامي',
        viewTop: 'العلوي',
        viewRight: 'الأيمن',
        viewIso: 'منظور',
        viewLayout: 'التخطيط',
      },
      drawing: {
        bomGenerate: 'إنشاء قائمة المواد',
        bomTitle: 'قائمة المواد',
        bomParts: 'أجزاء',
        bomNo: 'رقم',
        bomName: 'الاسم',
        bomSpec: 'المواصفة',
        bomQty: 'الكمية',
        bomMaterial: 'المادة',
        bomDimensions: 'الأبعاد',
        bomNotes: 'ملاحظات',
        partName: 'اسم الجزء',
        addRow: 'إضافة سطر',
        confirmAndContinue: 'تأكيد',
        customInput: 'إدخال مخصص...',
        attachments: 'مرفقات',
        weight: 'الوزن: ',
        untitled: 'بدون عنوان',
        dateLabel: 'سنة شهر يوم',
        scaleLabel: 'مقياس',
        itemNameLabel: 'الصنف',
        drawingNumberLabel: 'رقم الرسم',
        draftedBy: 'الراسم',
        checkedBy: 'المراجع',
        approvedBy: 'المعتمد',
        manufacturingDrawing: 'رسم تصنيعي',
        regenerate: 'إعادة الإنشاء',
        generating: 'جارٍ الإنشاء…',
        generate: 'إنشاء',
        strictModeError: '⚠ الوضع الصارم وجد {{count}} خطأ',
        bomReview: 'مراجعة/تحرير قائمة المواد',
        fitToView: 'ملاءمة العرض',
        exportSVG: 'تصدير SVG',
        exportPDF: 'تصدير PDF',
        statusQueued: 'في الانتظار',
        statusRunning: 'يعمل',
        statusReady: 'جاهز',
        statusFailed: 'فشل',
        cutListPartsTable: 'إنشاء جدول الأجزاء',
        cutListHeaderNumber: '#',
        cutListHeaderPart: 'الجزء',
        cutListHeaderKind: 'النوع',
        cutListHeaderWHT: 'W×H×T',
        cutListHeaderMaterial: 'المادة',
        cutListHeaderQty: 'الكمية',
        cutListFlattenSkipped: 'تم تخطي التسطيح: ',
      },
      services: {
        loginRequired: 'يلزم تسجيل الدخول',
        conversationIdRequired: 'مطلوب معرف المحادثة',
        conversationNotFound: 'لم يتم العثور على المحادثة',
        chatApiError: 'خطأ في API المحادثة',
        chatNoBody: 'تعذر الحصول على نص الاستجابة',
        chatNoResponse: 'لم يتم استلام استجابة من دالة المحادثة',
        errorPrefix: 'خطأ: ',
        intendedUseRequired: 'يرجى إدخال الاستخدام',
        cannotUpdateConversation: 'تعذّر تحديث المحادثة',
        intakeMismatch: 'الفئة والنوع الفرعي لا يتطابقان مع التفاصيل',
        untitledDesign: 'تصميم بلا عنوان',
        unitMm: 'مم',
        webglUnavailable: 'WebGL غير متوفر. يرجى استخدام متصفح يدعم WebGL.',
        noOutput: 'لا يوجد ناتج',
        drawingJobNotFound: 'مهمة الرسم غير موجودة',
        noSectionOutput: 'لا يوجد ناتج من ترجمة المقطع',
        noProjectionOutput: 'لا يوجد ناتج من ترجمة الإسقاط',
        noFileInArtifact: 'لا يوجد ملف {{ext}} في الناتج',
      },
      meshChat: {
        intro:
          'النماذج المجسمة منحوتة وليست بارامترية. لا يدعم التعديل في هذا الوضع. أعد الإنشاء للحصول على نتيجة مختلفة، أو ابدأ محادثة جديدة بوصف نصي للحصول على نموذج بارامتري.',
        regenerateButton: 'إعادة الإنشاء',
        regenerateFailedTitle: 'تعذّر بدء إعادة الإنشاء',
        downloadAria: 'تحميل {{format}}',
        downloadFailedTitle: 'تعذّر تحميل {{format}}',
      },
      meshError: {
        messageFailed: 'فشل إنشاء المجسم. جرّب صورة أخرى.',
        messageBanned: 'رفضت خدمة المجسمات الصورة. جرّب صورة أخرى.',
        messageExpired: 'لم يكتمل إنشاء المجسم. حاول مجددًا.',
        messageCancelled: 'لم يكتمل إنشاء المجسم. حاول مجددًا.',
        messageUnknown: 'لم يكتمل إنشاء المجسم. حاول مجددًا.',
        messageGeneric: 'فشل إنشاء المجسم.',
        retryButton: 'إعادة المحاولة',
        dismissButton: 'إغلاق',
        codeSuffix: '(الرمز {{code}})',
      },
      meshUnavailable: {
        message: 'المجسم غير متاح حاليًا. حاول إعادة الإنشاء من لوحة المحادثة.',
        reloadButton: 'إعادة التحميل',
      },
    },
    prompt: {
      morning: 'صباح الخير',
      afternoon: 'مرحبا',
      evening: 'مساء الخير',
      subtitlePrefix: 'املأ النموذج لإنشاء ',
      subtitleDrawing: 'رسومات ثنائية الأبعاد',
      subtitleMiddle: ' و',
      subtitleModel: 'نماذج ثلاثية الأبعاد',
      quickExamples: 'أمثلة سريعة:',
      examples: {
        gear: { label: 'ترس', intendedUse: 'ترس مستقيم بارامتري' },
        vase: { label: 'مزهرية', intendedUse: 'مزهرية بشكل عضوي' },
        architectureModel: { label: 'نموذج معماري', intendedUse: 'نموذج معماري لمنزل حديث' },
        bracket: { label: 'حامل', intendedUse: 'حامل تثبيت على شكل L' },
        cabinet: { label: 'خزانة جدارية', intendedUse: 'خزانة جدارية بـبابين' },
        diningTable: { label: 'طاولة طعام', intendedUse: 'طاولة طعام لستة أشخاص' },
      },
      loginRequiredTitle: 'خطأ',
      loginRequiredDescription: 'تحتاج إلى تسجيل الدخول.',
      generationFailed: 'فشل الإنشاء',
      newConversationTitle: 'محادثة جديدة',
      textStart: {
        headline: 'أو فقط صف ما تريد',
        subline:
          'اكتب جملة أو جملتين. أضف الحجم والمادة والغرض إذا أمكن. سنبدأ البناء من وصفك.',
        placeholder:
          'مثال: طاولة طعام من خشب البلوط 1200×800×720 مم بأربع أرجل مربعة مدببة.',
        buildButton: 'ابنِ من هذا',
        buildingButton: 'جارٍ البدء…',
      },
      imageStart: {
        headline: 'هل لديك صورة مرجعية؟',
        subline:
          'اسحب صورة لإنشاء مجسم ثلاثي الأبعاد منحوت (غير قابل للتعديل البارامتري).',
        starting: 'جارٍ بدء إنشاء المجسم…',
        pickButton: 'اختر صورة',
        invalidTypeTitle: 'نوع صورة غير مدعوم',
        invalidTypeDescription: 'يرجى رفع ملف JPEG أو PNG أو WEBP.',
        tooLargeTitle: 'حجم الصورة كبير جدًا',
        tooLargeDescription: 'الحد الأقصى 4 ميغابايت. غيّر الحجم وحاول مجددًا.',
        kickoffFailedTitle: 'تعذّر بدء إنشاء المجسم',
        kickoffFailedWhile: '(أثناء {{step}})',
        confirmHeadline: 'هل أنت جاهز للإنشاء؟',
        confirmButton: 'أنشئ مجسمًا ثلاثي الأبعاد',
        clearButton: 'مسح',
        previewAlt: 'معاينة الصورة المرجعية المرفوعة',
        uploading: 'جارٍ رفع الصورة…',
        uploadFailedTitle: 'تعذّر رفع الصورة',
        steps: {
          createConversation: 'إنشاء محادثة المسودة',
          uploadImage: 'رفع الصورة',
          startMesh: 'بدء إنشاء المجسم',
        },
      },
      meshModeToggle: {
        label: 'وضع المجسم (من صورة إلى ثلاثي الأبعاد)',
        imageHint: 'فعّل وضع المجسم لرفع صورة.',
        textHint: 'أوقف وضع المجسم لاستخدام الإدخال النصي.',
      },
    },
    intake: {
      category: '1. الفئة',
      subType: '2. النوع',
      quality: '3. مستوى الجودة',
      core: '4. التفاصيل الأساسية',
      details: '5. التفاصيل',
      drawing: '6. تفضيلات الرسم',
      freeform: '7. ملاحظات (اختياري)',
      inputError: 'خطأ في الإدخال',
      generate: 'إنشاء',
      intent: 'الطلب',
      intentPlaceholder: 'اختياري: طاولة طعام لستة أشخاص، حامل رف، علبة إلكترونيات...',
      advanced: 'تفاصيل متقدمة',
      hideAdvanced: 'إخفاء التفاصيل',
      coreLabels: {
        intendedUse: 'الاستخدام',
        intendedUsePlaceholder: 'طاولة طعام لستة أشخاص',
        size: 'الأبعاد',
        material: 'المادة',
        fastenerPreference: 'طريقة التثبيت (اختياري)',
      },
      dimensions: { length: 'الطول', width: 'العرض', height: 'الارتفاع' },
      freeformPlaceholder: 'أضف هنا المتطلبات التي لا تناسب النموذج (1000 حرف كحد أقصى)',
      categories: {
        furniture: { label: 'أثاث / نجارة', hint: 'طاولات وكراس ورفوف' },
        mechanical: { label: 'أجزاء ميكانيكية', hint: 'حوامل وتروس وصناديق' },
        architecture: { label: 'نماذج معمارية', hint: 'منازل ومبان ومواقع' },
        printing: { label: 'طباعة ثلاثية الأبعاد', hint: 'أدوات وحوامل وقواعد' },
      },
      subTypes: {
        table: 'طاولة', chair: 'كرسي', shelf: 'رف', bed: 'سرير', desk: 'مكتب', cabinet: 'خزانة',
        bracket: 'حامل', housing: 'صندوق', gear: 'ترس', shaft: 'عمود',
        fastener_part: 'مثبت', fixture: 'جيغ',
        building: 'مبنى', interior_room: 'غرفة داخلية', site: 'موقع', facade_detail: 'تفصيل واجهة',
        decorative: 'زخرفي', functional: 'جزء وظيفي', replacement: 'قطعة بديلة',
        miniature: 'مجسم صغير', enclosure: 'علبة',
      },
      qualityLevels: {
        draft: { label: 'مسودة', hint: 'شكل بسيط وسريع' },
        standard: { label: 'قياسي', hint: 'تفاصيل عادية' },
        manufacturing: { label: 'تصنيعي', hint: 'أبعاد وتفاوتات وقائمة مواد' },
        extreme: { label: 'أقصى تفصيل', hint: 'براغي ووصلات وكل التفاصيل' },
      },
      drawingPrefs: {
        scope: 'النطاق',
        section: 'المقاطع',
        tolerance: 'فئة التفاوت',
        holeTable: 'جدول الثقوب',
        bom: 'قائمة المواد',
        jisStrict: 'JIS صارم',
        scopes: { assembly_only: 'التجميع فقط', parts_only: 'الأجزاء فقط', both: 'كلاهما' },
        sections: { auto: 'تلقائي', none: 'بدون' },
        tolerances: { loose: 'واسع', standard: 'قياسي', tight: 'دقيق', precision: 'عالي الدقة' },
      },
      optionLabels: arOptionLabels,
      fieldLabels: arFieldLabels,
      units: {
        '段': 'رف',
        '本': 'شريحة',
        '個': '',
        '枚': '',
        '階': 'طابق',
        '棟': 'مبنى',
      },
    },
    auth: {
      heroTitleLine1: 'بالتعاون مع الذكاء الاصطناعي،',
      heroTitleLine2: 'تتحوّل أفكارك إلى تصاميم.',
      heroSubtitle: 'وكيلٌ للتصميم بمساعدة الحاسوب يحوّل النص إلى رسومات ثنائية الأبعاد ونماذج ثلاثية الأبعاد في ثوانٍ.',
      signupTitle: 'إنشاء حساب',
      loginTitle: 'مرحباً بعودتك',
      signupSubtitle: 'ابدأ تجربة مجانية لمدة سبعة أيام',
      loginSubtitle: 'سجّل الدخول إلى حسابك',
      emailLabel: 'البريد الإلكتروني',
      emailPlaceholder: 'you@example.com',
      passwordLabel: 'كلمة المرور',
      passwordPlaceholder: 'ستة أحرف على الأقل',
      submitSignup: 'إنشاء حساب',
      submitLogin: 'تسجيل الدخول',
      submitting: '...',
      hasAccount: 'هل لديك حسابٌ بالفعل؟',
      noAccount: 'ليس لديك حساب؟',
      toggleToLogin: 'تسجيل الدخول',
      toggleToSignup: 'إنشاء حساب',
      errors: {
        invalidCredential: 'البريد الإلكتروني أو كلمة المرور غير صحيحَين',
        emailInUse: 'هذا البريد الإلكتروني مستخدمٌ بالفعل',
        weakPassword: 'يجب أن تتكوّن كلمة المرور من ستة أحرف على الأقل',
        tooManyRequests: 'عدد المحاولات كبير. يُرجى المحاولة لاحقاً',
        unknown: 'حدث خطأ. يُرجى المحاولة مرّةً أخرى',
      },
    },
    landing: {
      navLogin: 'تسجيل الدخول',
      navStartFree: 'ابدأ مجاناً',
      heroTitle1: 'بالكلمات وحدها،',
      heroTitle2: 'صمّم.',
      heroSubtitle: 'أنشئ من النص رسومات ثنائية الأبعاد ونماذج ثلاثية الأبعاد بارامترية في ثوانٍ. AgentCAD يُعيد تعريف التصميم.',
      heroCta: 'تجربة مجانية لسبعة أيام',
      heroCtaNote: 'لا حاجة إلى بطاقة ائتمان',
      statTimeSaved: 'توفير وقت التصميم',
      statSetup: 'وقت الإعداد',
      statSetupSuffix: 'ث',
      statCost: 'تكلفة البدء',
      statCostSuffix: '$',
      statTimeSuffix: '%',
      featuresEyebrow: 'المميّزات',
      featuresHeadline: 'كلّ شيءٍ يبدأ من النص.',
      features: {
        textTo2d3d: {
          title: 'من النص إلى 2D و3D',
          tag: 'حوّل ما يدور في خيالك إلى شكلٍ ملموس.',
          desc: 'اكتب وصفاً فقط — يُنشئ الذكاء الاصطناعي نموذجاً بارامترياً قائماً على OpenSCAD ورسماً ثنائي الأبعاد تلقائياً.',
        },
        realtime: {
          title: 'تحريرٌ لحظي',
          tag: 'تعديلاتٌ في طرفة عين.',
          desc: 'غيّر الحجم والشكل والزاوية لحظياً عبر منزلقات، دون انتظار إعادة الإنشاء.',
        },
        multiAngle: {
          title: 'عرضٌ من كلّ زاوية',
          tag: 'تحقّق من تصميمك من أيّ منظور.',
          desc: 'دورانٌ حرّ بزاوية 360°. تحقّق لحظياً من أيّ زاوية تشاء.',
        },
        export: {
          title: 'تصدير',
          tag: 'جاهزٌ للتسليم.',
          desc: 'تصدير بنقرةٍ واحدة إلى صيغتَي PDF وSTL.',
        },
      },
      stepsEyebrow: 'كيف تعمل',
      stepsHeadline: 'ثلاث خطواتٍ فحسب.',
      steps: {
        describe: { title: 'اشرح بالنص', desc: 'اكتب ما تريد إنشاءه — لا تحتاج إلى أيّ خبرةٍ متخصّصة.' },
        generate: { title: 'إنشاءٌ آليٌّ بالذكاء الاصطناعي', desc: 'رسومات ثنائية الأبعاد ونماذج ثلاثية الأبعاد بارامترية في الحال.' },
        editExport: { title: 'حرّر وصدّر', desc: 'اضبط البارامترات ثم نزّل النتيجة النهائية.' },
      },
      useCasesEyebrow: 'حالات الاستخدام',
      useCasesHeadline: 'لماذا تختار الفِرَق AgentCAD',
      useCasesSub: 'وكيل تصميمٍ مخصّصٌ لك، يعمل على مدار الساعة طوال أيام الأسبوع.',
      useCases: {
        prototype: {
          title: 'نماذج أوّلية أسرع بعشر مرّات',
          audience: 'لفِرَق تطوير المنتجات',
          desc: 'حوّل الفكرة إلى نموذجٍ ثلاثيّ الأبعاد فوراً، واختصِر دورة التطوير.',
        },
        nonExpert: {
          title: 'دون الحاجة إلى خبرةٍ في التصميم بالحاسوب',
          audience: 'للمصمّمين ومديري المنتجات',
          desc: 'إدخالٌ نصّيٌّ بديهيّ — صمّم دون الحاجة إلى مهارات CAD.',
        },
        education: {
          title: 'النمذجة ثلاثية الأبعاد في التعليم',
          audience: 'للمدارس وبرامج التدريب',
          desc: 'تجربة تعلّمٍ جديدة تجمع بين البرمجة والنمذجة ثلاثية الأبعاد.',
        },
      },
      pricingEyebrow: 'الأسعار',
      pricingHeadline: 'خططٌ بسيطة',
      pricingSub: 'تشمل جميع الخطط تجربةً مجانية لسبعة أيام. يمكنك الإلغاء في أيّ وقت.',
      planTryFree: 'جرّب مجاناً',
      planRecommended: 'الموصى به',
      planUnit: '/شهرياً',
      plans: {
        starter: { name: 'مبتدئ', price: '$98' },
        pro: { name: 'احترافي', price: '$298' },
        business: { name: 'أعمال', price: '$980' },
      },
      planFeatureLabels: {
        generations: 'عدد عمليات الإنشاء',
        gen2d3d: 'إنشاء 2D و3D',
        parametricEdit: 'التحرير البارامتري',
        export: 'التصدير',
        history: 'حفظ السجلّ',
        priorityQueue: 'الأولوية في الطابور',
        teamSeats: 'مقاعد الفريق',
        support: 'الدعم',
      },
      planFeatureValues: {
        starterGenerations: '50 شهرياً',
        proGenerations: '300 شهرياً',
        businessGenerations: 'غير محدود',
        starterHistory: '30 يوماً',
        proHistory: 'غير محدود',
        businessHistory: 'غير محدود',
        starterSeats: 'مقعدٌ واحد',
        proSeats: 'مقعدٌ واحد',
        businessSeats: 'حتى 10 مقاعد',
      },
      finalCtaTitle1: 'أعِد ابتكار',
      finalCtaTitle2: 'التصميم.',
      finalCtaSub: 'أوكِل مهمّة التصميم إلى وكيلك الذكي.',
      finalCtaButton: 'تجربة مجانية لسبعة أيام',
      finalCtaNote: 'لا حاجة إلى بطاقة ائتمان · يمكن الإلغاء في أيّ وقت',
      footerCopyright: '© 2026 AgentCAD',
      footerTerms: 'شروط الاستخدام',
      footerPrivacy: 'سياسة الخصوصية',
      footerContact: 'تواصل معنا',
    },
    contact: {
      backToTop: 'العودة إلى الأعلى',
      title: 'تواصل معنا',
      description: 'نرحّب بأسئلتكم واقتراحاتكم وبلاغاتكم عن الأعطال — لا تتردّدوا في التواصل معنا.',
      submittedMessage: 'سيُفتح تطبيق البريد الإلكتروني لديك. يُرجى إتمام الإرسال.',
      sendAgain: 'إرسالٌ مرّةً أخرى',
      nameLabel: 'الاسم',
      namePlaceholder: 'اسمك الكامل',
      emailLabel: 'البريد الإلكتروني',
      emailPlaceholder: 'you@example.com',
      messageLabel: 'الرسالة',
      messagePlaceholder: 'اكتب رسالتك هنا...',
      submit: 'إرسال',
      mailtoSubject: 'استفسارٌ بشأن AgentCAD: {{name}}',
      mailtoBody: 'الاسم: {{name}}\nالبريد الإلكتروني: {{email}}\n\n{{message}}',
    },
    privacy: {
      backToTop: 'العودة إلى الأعلى',
      title: 'سياسة الخصوصية',
      lastUpdated: 'آخر تحديث: 15 مارس 2026',
      sections: {
        collect: {
          heading: 'أوّلاً: المعلومات التي نقوم بجمعها',
          intro: 'تجمع الشركة المعلومات التالية لأغراض تقديم الخدمة وتشغيلها:',
          items: [
            'بيانات الحساب، كعنوان البريد الإلكتروني وكلمة المرور.',
            'بيانات المشاريع التي يُنشئها المستخدم داخل الخدمة.',
            'سجلّات الاستخدام، وتشمل عنوان بروتوكول الإنترنت (IP) ومعلومات المتصفّح وتواريخ الوصول وأوقاته.',
            'المعلومات التقنية، بما فيها ملفّات تعريف الارتباط (Cookies) وما يماثلها.',
          ],
        },
        purpose: {
          heading: 'ثانياً: أغراض استخدام المعلومات',
          items: [
            'تقديم الخدمة وتشغيلها وتحسينها.',
            'تقديم الدعم الفنّي للمستخدمين.',
            'إرسال الإشعارات الجوهرية المتعلّقة بالخدمة.',
            'تحليل الاستخدام وإعداد الإحصاءات.',
            'الكشف عن الاستخدام غير المشروع ومنعه.',
          ],
        },
        thirdParty: {
          heading: 'ثالثاً: مشاركة المعلومات مع الغير',
          intro: 'لا تفصح الشركة عن المعلومات الشخصية للمستخدم لأيّ طرفٍ ثالث، إلّا في الحالات الآتية:',
          items: [
            'بناءً على موافقةٍ صريحةٍ من المستخدم.',
            'متى استلزم القانون أو الجهات المختصّة ذلك.',
            'عند الضرورة لحماية حياة شخصٍ ما، أو سلامة بدنه، أو ممتلكاته.',
            'مشاركتها مع مقدّمي الخدمات اللازمين لتشغيل الخدمة (كـ Firebase وGoogle Cloud وما في حكمهما).',
          ],
        },
        storage: {
          heading: 'رابعاً: حفظ البيانات وأمنها',
          body: 'تُخزَّن بيانات المستخدم على منصّة Google Cloud Platform بصورةٍ آمنة. وتتّخذ الشركة التدابير الفنّية والإدارية المناسبة لمنع الوصول غير المصرَّح به إلى البيانات أو تسرّبها أو العبث بها.',
        },
        cookies: {
          heading: 'خامساً: استخدام ملفّات تعريف الارتباط',
          body: 'تستخدم الخدمة ملفّات تعريف الارتباط لإدارة حالة المصادقة وتحليل الاستخدام. ويجوز للمستخدم تعطيلها من إعدادات المتصفّح، علماً بأنّ ذلك قد يؤدّي إلى تعطيل بعض الميزات.',
        },
        rights: {
          heading: 'سادساً: حقوق المستخدم',
          intro: 'يحقّ للمستخدم ممارسة الحقوق التالية:',
          items: [
            'طلب الاطّلاع على بياناته الشخصية أو تصحيحها أو حذفها.',
            'إغلاق حسابه.',
            'الحصول على نسخةٍ من بياناته (تصدير البيانات).',
          ],
        },
        changes: {
          heading: 'سابعاً: تعديل هذه السياسة',
          body: 'يجوز للشركة تعديل هذه السياسة من حينٍ إلى آخر. وفي حال إجراء تعديلاتٍ جوهرية، تُخطَر بها الجهات المعنية عبر الخدمة.',
        },
        contact: {
          heading: 'ثامناً: التواصل بشأن الخصوصية',
          bodyPrefix: 'لأيّ استفسارٍ يتعلّق بالخصوصية، يُرجى التواصل عبر ',
          link: 'صفحة التواصل',
          bodySuffix: '.',
        },
      },
    },
    terms: {
      backToTop: 'العودة إلى الأعلى',
      title: 'شروط الاستخدام',
      lastUpdated: 'آخر تحديث: 15 مارس 2026',
      sections: {
        application: {
          heading: 'المادة (1) — النطاق',
          body: 'تُنظّم شروط الاستخدام هذه (ويُشار إليها فيما يلي بـ«الشروط») قواعد استخدام خدمة AgentCAD (ويُشار إليها فيما يلي بـ«الخدمة»). ويُعدّ المستخدم بمجرّد استخدامه للخدمة موافقاً على هذه الشروط ومُلتزماً بها.',
        },
        account: {
          heading: 'المادة (2) — الحساب',
          body: 'يلتزم المستخدم بتقديم بياناتٍ صحيحةٍ ومحدَّثة عند التسجيل. ويتحمّل المستخدم وحده مسؤولية المحافظة على سرّية بيانات حسابه، ويُحظَر عليه إعارة حسابه أو نقله إلى الغير بأيّ صورةٍ كانت.',
        },
        serviceContent: {
          heading: 'المادة (3) — مضمون الخدمة',
          body: 'الخدمة عبارةٌ عن أداةٍ للتصميم بمساعدة الحاسوب (CAD) تعتمد على الذكاء الاصطناعي، وتُنشئ من الإدخال النصّي رسوماتٍ ثنائية الأبعاد ونماذج ثلاثية الأبعاد بارامترية. ويجوز للشركة تعديل مضمون الخدمة في أيّ وقتٍ ودون إشعارٍ مسبق.',
        },
        prohibited: {
          heading: 'المادة (4) — الأفعال المحظورة',
          items: [
            'الأفعال المخالفة للأنظمة والقوانين.',
            'كلّ فعلٍ من شأنه عرقلة تشغيل الخدمة أو الإضرار بها.',
            'كلّ فعلٍ يُلحق ضرراً بمستخدمين آخرين.',
            'الهندسة العكسية، والوصول غير المصرّح به إلى الخدمة أو إلى أنظمتها.',
            'استخدام الخدمة في إنتاج محتوى مخالفٍ للقانون.',
          ],
        },
        ip: {
          heading: 'المادة (5) — حقوق الملكية الفكرية',
          body: 'تؤول حقوق المؤلِّف على المحتوى الذي يُنشئه المستخدم باستخدام الخدمة إلى المستخدم نفسه، في الحدود التي تُجيزها التشريعات المعمول بها. أمّا حقوق الملكية الفكرية المتعلّقة بالخدمة ذاتها — بما في ذلك البرمجيات والتصميم والعلامات التجارية — فتظلّ ملكاً خالصاً للشركة.',
        },
        disclaimer: {
          heading: 'المادة (6) — إخلاء المسؤولية',
          body: 'تُقدَّم الخدمة على أساس «كما هي» (As Is)، ولا تُقدّم الشركة أيّ ضمانٍ صريحٍ أو ضمنيّ بشأن دقّة المخرجات الناتجة عن الذكاء الاصطناعي أو اكتمالها أو ملاءمتها لغرضٍ معيّن. ولا تتحمّل الشركة المسؤولية عن أيّ أضرارٍ تنشأ عن استخدام الخدمة، وذلك إلى أقصى حدٍّ يُجيزه القانون.',
        },
        changes: {
          heading: 'المادة (7) — تعديل الخدمة وإيقافها',
          body: 'يجوز للشركة تعديل الخدمة، أو إيقاف تقديمها كلّياً أو جزئياً، في أيّ وقتٍ ودون إشعارٍ مسبق. ولا تتحمّل الشركة المسؤولية عن أيّ أضرارٍ قد تلحق بالمستخدم نتيجةً لذلك.',
        },
        law: {
          heading: 'المادة (8) — القانون الواجب التطبيق والاختصاص القضائي',
          body: 'تخضع هذه الشروط لأحكام القانون الياباني وتُفسَّر وفقاً له. ويكون الاختصاص الحصري للنظر في أيّ نزاعٍ ينشأ عن الخدمة أو يتّصل بها لمحكمة طوكيو المحلية بوصفها محكمة الدرجة الأولى المختصّة.',
        },
      },
    },
    errorPage: {
      title: 'حدث خطأ',
      description: 'نعتذر، حدث خطأٌ أثناء تحميل هذه الصفحة.\nيُرجى التواصل معنا للمساعدة في حلّ المشكلة.',
      home: 'العودة إلى الرئيسية',
    },
  },
} satisfies Resource;
